import { useState, useMemo, useEffect, useRef } from 'react';
import { fechaDelEstadoDeCuenta } from '../fechaDelEstadoDeCuenta.js';
import {
  AlertTriangle, Calendar, CheckCircle, ExternalLink, FileText, Plus,
  Save, Search, Trash2, Upload,
} from 'lucide-react';
import { Modal, Input, Select, Textarea, StatCard, Avatar, ClientSearchSelect } from './ui.jsx';
import { ClaimsReportView } from './ClaimsReportView.jsx';
import { toast } from './Toast.jsx';
import { uid, fmt, daysUntil, fmtMoney, fmtMonth, MONTHS_LIST, renewalLeadDaysForType } from '../utils.js';
import { leadTypeLabel } from './PoliciesView.jsx';
import { hasSeatPermission, normalizeSeatPermissions, canDelete } from '../seatPermissions.js';
// El cuadre del historial vive suelto, sin React, para poder probar desde el
// servidor que las dos mitades suman SIEMPRE el total (Maricarmen, 2026-09-10).
import { cuadreDeComisiones, vieneDeUnaSubida, filasFueraDeSubidas } from '../subidasDeComisiones.js';
import { esperaComision } from '../cuentaEnComisiones.js';
import { deleteCommission as apiDeleteCommission, importCommissionBatch, createClient as apiCreateClient, createCommission as apiCreateCommission, updateCommission as apiUpdateCommission } from '../api.js';

// Map a carrier "prod" / "product" / "coverage" column value to one of our
// commission policy types, so a statement that mixes lines (e.g. Florida Blue
// Health + Dental in one file, distinguished by a "prod" column) imports each
// row with the RIGHT type instead of one type for the whole file (Lissette
// 2026-07-13). Returns null when the value doesn't clearly name a line, so the
// caller falls back to the type picked at upload.
function productToPolicyType(s) {
  const t = String(s || '').toLowerCase();
  if (!t) return null;
  if (/dental/.test(t)) return 'dental';
  if (/vision/.test(t)) return 'vision';
  if (/\b(medicare|mapd|pdp|advantage|d-?snp)\b/.test(t)) return 'medicare';
  if (/\b(life|final\s*expense|iul|term life|whole life)\b/.test(t)) return 'life';
  if (/\b(auto|automobile)\b/.test(t)) return 'auto';
  if (/\b(home|homeowner|property)\b/.test(t)) return 'home';
  if (/\b(health|medical|aca|marketplace|major\s*medical|hmo|ppo|epo|mkt)\b/.test(t)) return 'aca';
  return null;
}

// ── Fuzzy name matching for commission verification ──────────────────────
// These were referenced by runFuzzyMatch but never defined, so the match
// step threw "buildClientVariants is not defined" inside a setTimeout and
// the modal hung forever on "Processing...". Implemented here: pure,
// dependency-free, and null-safe (clients can have missing name fields).

// Lowercase, strip accents, drop punctuation, collapse whitespace.
function _normName(s) {
  // NFD splits an accented letter into base + combining mark. The combining
  // mark must be DELETED, not turned into a separator: the punctuation pass
  // below replaces with a space, so relying on it split "Pérez" into
  // "pe rez" and the name then matched nothing. Accented names are the norm
  // in this book, so every "José"/"Pérez" row silently stayed "Not in CRM".
  // Strip the combining range first, then let punctuation become a space
  // (so "Maria-Jose" still tokenizes as two words).
  return String(s == null ? '' : s)
    .normalize('NFD')
    .toLowerCase()
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Levenshtein distance (two-row DP). Names are short so this is cheap.
function _lev(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = [];
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[b.length];
}

function _ratio(a, b) {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  return 1 - _lev(a, b) / Math.max(a.length, b.length);
}

// Client name parts + full-name forms for scoring. `first` and `last` are
// the normalized first/last (may be multi-token, e.g. "rosales lopez").
function buildClientVariants(c) {
  if (!c) return { forms: [], first: '', last: '' };
  const first = _normName(c.firstName);
  const last = _normName(c.lastName);
  const full = _normName(c.name);
  const forms = new Set();
  if (first && last) { forms.add(`${first} ${last}`); forms.add(`${last} ${first}`); }
  if (full) forms.add(full);
  for (const v of [...forms]) {
    const sorted = v.split(' ').filter(Boolean).sort().join(' ');
    if (sorted) forms.add(sorted);
  }
  return { forms: [...forms].filter(Boolean), first, last };
}

// Best similarity (0..1) between a raw file name and a client. HARD RULE
// (Lissette 2026-05-18): a real match requires BOTH the client's first
// AND last name to be present in the file name — never a single-name hit.
// If either is missing the score is 0 so the row stays "Not in CRM"
// rather than being mis-assigned to the wrong person.
function bestMatchScoreForClient(rawName, cv) {
  const raw = _normName(rawName);
  if (!raw || !cv || !cv.first || !cv.last) return 0;
  const rawTokens = new Set(raw.split(' ').filter(Boolean));
  // Every token of a name part must appear in the file name (exact token,
  // or a close fuzzy token for typos/accents). Handles compound first/last
  // names like "Maria Candelaria" / "Rosales Lopez".
  const allPartTokensPresent = (part) => {
    const parts = part.split(' ').filter(Boolean);
    if (!parts.length) return false;
    return parts.every(p =>
      rawTokens.has(p) || [...rawTokens].some(t => _ratio(t, p) >= 0.85)
    );
  };
  if (!allPartTokensPresent(cv.first) || !allPartTokensPresent(cv.last)) return 0;
  // Both names confirmed present → confident match. Refine with
  // whole-string similarity (order-independent) but floor at 0.85.
  const rawSorted = [...raw.split(' ').filter(Boolean)].sort().join(' ');
  let best = 0;
  for (const f of cv.forms) {
    if (!f) continue;
    if (f === raw) return 1;
    best = Math.max(
      best,
      _ratio(raw, f),
      _ratio(rawSorted, [...f.split(' ').filter(Boolean)].sort().join(' ')),
    );
  }
  return Math.max(best, 0.85);
}

// ── Commission → client resolution ──────────────────────────────────────────
// Module-level (not inside CommissionTracker) because ReportsView needs the
// same answer for the Reclamos tab. It used to read a `commissionClientIds`
// that only existed in CommissionTracker's scope, so opening Reclamos threw
// "ReferenceError: commissionClientIds is not defined" and the tab crashed.

// Stable key that survives the {...r} spread in normalizedComms/monthComms
// (object identity does NOT, and legacy rows may have no id).
function commKey(r) {
  return r.id || `${r.clientNameRaw||r.name||""}|${r.carrier||""}|${r.month||""}|${r.amount||""}|${r.importedAt||""}`;
}

// "Remembered assignments" (Lissette 2026-07-01): once a raw carrier name has
// been linked to a client (any commission with a stored clientId), remember
// that name -> client so future uploads of the SAME name don't ask again —
// they auto-link and show "Asignado a X". Keyed by the normalized name so
// accents/case/format differences still match.
function buildNameAliasMap(commissions, clients) {
  const m = new Map();
  const valid = new Set((clients || []).map(c => c.id));
  for (const r of (commissions || [])) {
    if (r.clientId && valid.has(r.clientId)) {
      const nm = _normName(r.clientNameRaw || r.name || "");
      if (nm && !m.has(nm)) m.set(nm, r.clientId);
    }
  }
  return m;
}

// LIVE name resolution: a commission belongs to a client if it has a valid
// stored clientId OR its raw file name now matches a client by first+last.
// Creating the client in the CRM later makes its "Not in CRM" commissions move
// to Paid automatically — no re-upload, no manual Assign.
function buildResolvedClientIdMap(commissions, clients, nameAliasMap) {
  const map = new Map();
  const clientById = new Map((clients || []).map(c => [c.id, c]));
  const variants = (clients || []).map(c => ({ id: c.id, v: buildClientVariants(c) }));
  for (const r of (commissions || [])) {
    const key = commKey(r);
    if (r.clientId && clientById.has(r.clientId)) { map.set(key, r.clientId); continue; }
    const nm = r.clientNameRaw || r.name || "";
    if (!nm) { map.set(key, null); continue; }
    // Remembered assignment wins over fuzzy matching.
    const aliasId = nameAliasMap.get(_normName(nm));
    if (aliasId && clientById.has(aliasId)) { map.set(key, aliasId); continue; }
    let best = 0, bestId = null;
    for (const cv of variants) {
      const s = bestMatchScoreForClient(nm, cv.v);
      if (s > best) { best = s; bestId = cv.id; }
    }
    map.set(key, best >= 0.65 ? bestId : null);
  }
  return map;
}

// Clients that HAVE a received commission this year (positive amount, matched
// to a CRM client). The Reclamos tab uses this to show "Comisión pagada / no
// pagada" per policy — that report is about COMMISSIONS, never about the
// client's monthly premium (Lissette 2026-07-13: "en reclamos solamente es
// las comisiones no pagas").
function clientIdsWithPaidCommission(commissions, resolvedClientIdByComm) {
  const yr = String(new Date().getFullYear());
  const set = new Set();
  for (const r of (commissions || [])) {
    if (!(parseFloat(r.amount || 0) > 0)) continue; // positive = money received
    if (r.month && !String(r.month).startsWith(yr)) continue; // this year only
    const cid = resolvedClientIdByComm.get(commKey(r));
    if (cid) set.add(cid);
  }
  return set;
}

// One-shot version for callers that don't already hold the resolution map.
function computeCommissionClientIds(commissions, clients) {
  const alias = buildNameAliasMap(commissions, clients);
  const resolved = buildResolvedClientIdMap(commissions, clients, alias);
  return clientIdsWithPaidCommission(commissions, resolved);
}

// Searchable client assign control. A native <select> with hundreds of
// clients is unusable — this opens an inline type-to-filter list. Inline
// (in normal flow, not absolute) so it never gets clipped by the modal's
// scroll container. Light popover reads on both the light modal and the
// dark tracker.
function ClientPicker({ clients, onPick, lang }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const sorted = useMemo(
    () => [...clients].sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '')),
    [clients],
  );
  const term = q.trim().toLowerCase();
  const filtered = term
    ? sorted.filter(c => `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(term)
        || `${c.lastName || ''} ${c.firstName || ''}`.toLowerCase().includes(term))
    : sorted;
  if (!open) {
    return (
      <button type="button" onClick={() => { setQ(''); setOpen(true); }}
        style={{ fontSize: '.7rem', fontWeight: 700, background: '#fff', border: '1px solid #cbd5e1', color: '#334155', borderRadius: 7, padding: '.32rem .6rem', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
        {lang === 'es' ? 'Asignar…' : 'Assign…'} ▾
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 210, flexShrink: 0 }}>
      <input autoFocus value={q} onChange={e => setQ(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        placeholder={lang === 'es' ? 'Escribe un nombre…' : 'Type a name…'}
        style={{ fontSize: '.72rem', padding: '.35rem .5rem', borderRadius: 6, border: '1px solid #94a3b8', background: '#fff', color: '#0f172a', outline: 'none', width: '100%' }} />
      <div style={{ maxHeight: 168, overflowY: 'auto', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' }}>
        {filtered.length === 0 && (
          <div style={{ padding: '.5rem', fontSize: '.7rem', color: '#94a3b8' }}>{lang === 'es' ? 'Sin resultados' : 'No results'}</div>
        )}
        {filtered.slice(0, 80).map(c => (
          <div key={c.id} onMouseDown={e => e.preventDefault()}
            onClick={() => { onPick(c.id); setOpen(false); }}
            style={{ padding: '.35rem .55rem', fontSize: '.72rem', color: '#1e293b', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px solid #f1f5f9' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            {c.lastName}, {c.firstName}
          </div>
        ))}
      </div>
      <button type="button" onClick={() => setOpen(false)}
        style={{ fontSize: '.66rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'flex-end', padding: '.15rem .3rem' }}>
        {lang === 'es' ? 'Cancelar' : 'Cancel'}
      </button>
    </div>
  );
}

// Compact multi-select filter (dark tracker theme). `selected` is an array;
// an empty array means "all". Used so several carriers / states can be
// picked at once instead of the old single-choice <select>.
function MultiSelectFilter({ options, selected, onChange, allLabel, icon, lang, width = 210 }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const label = selected.length === 0
    ? allLabel
    : selected.length === 1 ? selected[0] : `${selected.length} ${lang === 'es' ? 'seleccionadas' : 'selected'}`;
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display:'flex',alignItems:'center',gap:'.5rem',background:'var(--c-surface2,#112538)',border:`1px solid ${selected.length?'var(--c-cyan,#00d4ff)':'var(--c-border,#1a3550)'}`,borderRadius:9,padding:'.45rem .75rem',cursor:'pointer',color:'var(--c-white,#eef6ff)',fontWeight:700,fontSize:'.78rem',fontFamily:'inherit',whiteSpace:'nowrap',maxWidth:width }}>
        {icon}
        <span style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{label}</span>
        <span style={{ fontSize:'.6rem', color:'var(--c-text3,#45688a)' }}>▾</span>
      </button>
      {open && <>
        <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:40 }}/>
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:41, width, maxHeight:280, overflowY:'auto', background:'var(--c-surface,#0d1c2e)', border:'1px solid var(--c-border2,#1f3f60)', borderRadius:10, boxShadow:'0 12px 32px rgba(0,0,0,.45)', padding:'.35rem' }}>
          <button type="button" onClick={() => onChange([])}
            style={{ display:'flex',alignItems:'center',gap:'.5rem',width:'100%',textAlign:'left',background:selected.length===0?'rgba(0,212,255,.12)':'transparent',border:'none',borderRadius:7,padding:'.4rem .55rem',cursor:'pointer',color:'var(--c-white,#eef6ff)',fontSize:'.74rem',fontWeight:700,fontFamily:'inherit' }}>
            <span style={{ width:14,display:'inline-block',color:'var(--c-cyan,#00d4ff)' }}>{selected.length===0?'✓':''}</span>{allLabel}
          </button>
          {options.map(o => (
            <button key={o} type="button" onClick={() => toggle(o)}
              style={{ display:'flex',alignItems:'center',gap:'.5rem',width:'100%',textAlign:'left',background:selected.includes(o)?'rgba(0,212,255,.1)':'transparent',border:'none',borderRadius:7,padding:'.4rem .55rem',cursor:'pointer',color:'var(--c-text,#c8dff0)',fontSize:'.74rem',fontFamily:'inherit' }}>
              <span style={{ width:14,display:'inline-block',color:'var(--c-cyan,#00d4ff)' }}>{selected.includes(o)?'✓':''}</span>
              <span style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{o}</span>
            </button>
          ))}
          {options.length===0 && <div style={{ padding:'.5rem', fontSize:'.7rem', color:'var(--c-text3,#45688a)' }}>{lang==='es'?'Sin opciones':'No options'}</div>}
        </div>
      </>}
    </div>
  );
}

export function CommissionTracker({ clients, commissions, onCommissionsChange, onClientsChange, users, currentUser, t, lang, policyTypes, policies = [], onClientClick }) {
  const isOwner = currentUser?.role !== "assistant"; const isAgencyAdmin = ["agency_owner","agency_admin"].includes(currentUser?.role);
  // Write access. A seat needs `commissions.edit` — the SAME key the backend
  // gates the write endpoints with. Before this the buttons were shown to
  // every seat and the 403 only landed on the final save, after the whole
  // file had been parsed and verified (154 rows lost, 2026-08-10).
  const seatPerms = normalizeSeatPermissions(currentUser?.seatPermissions);
  const canEditComm = isOwner || hasSeatPermission(seatPerms, "commissions.edit");
  // Deleting needs the cross-cutting deletion gate on top of module write
  // access — mirrors the backend's DELETE /commissions/:id check.
  const canDeleteComm = canEditComm && canDelete(currentUser);
  // "+ Cliente" creates a CRM client, so it needs the clients module too.
  const canAddClientFromComm = canEditComm && (isOwner || hasSeatPermission(seatPerms, "clients.edit"));
  // Client name — clickable (opens the client card) when the parent wired
  // onClientClick, otherwise a plain label. Same visual weight either way.
  // El "lead source" de la agencia es el TIPO DE LEAD de la póliza —Personal,
  // Facebook, Referido, Broker…— no el campo `source` de la ficha, que es texto
  // libre y en los clientes importados dice literalmente "Importación pólizas".
  // Mirar ahí fue el error de la primera versión: el chip salía vacío o decía
  // una cosa que no era (Lissette 2026-08-17: "no lo veo").
  // Tampoco confundirlo con `record.source`, que dice si la comisión se subió
  // por archivo o se escribió a mano.
  const policiesByClientId = useMemo(() => {
    const m = new Map();
    for (const p of (policies || [])) {
      if (!p?.clientId) continue;
      if (!m.has(p.clientId)) m.set(p.clientId, []);
      m.get(p.clientId).push(p);
    }
    return m;
  }, [policies]);
  // Una comisión no apunta a una póliza concreta, solo trae la compañía. Así
  // que se prefiere la póliza de ESA compañía; si no hay, la primera del
  // cliente que tenga tipo de lead.
  const leadTypeFor = (client, carrier) => {
    const ps = policiesByClientId.get(client?.id) || [];
    const norm = (x) => String(x || '').trim().toLowerCase();
    const byCarrier = carrier ? ps.find((p) => norm(p.carrier) === norm(carrier) && p.leadType) : null;
    const hit = byCarrier || ps.find((p) => p.leadType);
    return hit ? leadTypeLabel(hit.leadType, lang) : '';
  };
  const LeadSourceChip = ({ label }) => {
    if (!label) return null;
    return <span title={lang==="es"?"Tipo de lead de la póliza":"Policy lead type"}
      style={{fontSize:".6rem",padding:".1rem .4rem",borderRadius:5,background:"rgba(52,211,153,.1)",color:"#34d399",fontWeight:700,whiteSpace:"nowrap",maxWidth:150,overflow:"hidden",textOverflow:"ellipsis"}}>
      🎯 {label}</span>;
  };
  const ClientName = ({ client, color, carrier }) => (
    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",minWidth:0}}>
      {onClientClick
        ? <button type="button" onClick={()=>onClientClick(client)} title={lang==="es"?"Abrir ficha del cliente":"Open client"}
            style={{display:"inline-flex",alignItems:"center",gap:4,background:"none",border:"none",padding:0,cursor:"pointer",fontWeight:600,fontSize:".84rem",color:"var(--c-cyan,#00d4ff)",fontFamily:"inherit"}}>
            {client.firstName} {client.lastName}<ExternalLink size={11} style={{flexShrink:0,opacity:.8}}/>
          </button>
        : <div style={{fontWeight:600,fontSize:".84rem",color}}>{client.firstName} {client.lastName}</div>}
      <LeadSourceChip label={leadTypeFor(client, carrier)}/>
    </div>);
  // Access is governed by the backend-backed `commissions.view` seat permission:
  // seats only reach this view if their permission grants it (nav-gated upstream),
  // and owners/agents always have access. No local-only access list.
  const hasAccess = true;
  const [month, setMonth] = useState("all");
  const [tab, setTab] = useState("tracker"); // tracker | upload | perms
  const [kpiFilter, setKpiFilter] = useState("all"); // all | paid | notpaid | inactive | notincrm
  const [policyTypeFilter, setPolicyTypeFilter] = useState("all");
  // Tracker: find one client by name + narrow by CRM status (Lissette 2026-07-13).
  const [trackerSearch, setTrackerSearch] = useState("");
  const [trackerStatus, setTrackerStatus] = useState("all"); // all | active | inactive
  // Multi-select filters: an empty array means "all". Replaces the old
  // single-carrier <select> so several companies can be chosen at once,
  // and adds a by-state filter (Lissette 2026-07-01).
  const [carrierSel, setCarrierSel] = useState([]);
  const [stateSel, setStateSel] = useState([]);
  const carrierMatch = (c) => carrierSel.length === 0 || carrierSel.includes(c.carrier || "");
  // Se compara contra el estado EFECTIVO (columna del statement, o el de la
  // ficha del cliente si el archivo no la trae). commState se declara más
  // abajo, después de la resolución de clientes; aquí solo se cierra sobre él y
  // no se llama hasta que monthComms filtra.
  const stateMatch = (c) => stateSel.length === 0 || stateSel.includes(commState(c));
  const [yearFilter, setYearFilter] = useState(() => String(new Date().getFullYear()));
  const [uploadModal, setUploadModal] = useState(false);
  const [manualModal, setManualModal] = useState(false);
  const [carrierName, setCarrierName] = useState("");
  const [uploadPolicyType, setUploadPolicyType] = useState("aca");
  const [matchResults, setMatchResults] = useState(null); // null | [{rawName, amount, carrier, matched, confirmed}]
  const [manualForm, setManualForm] = useState({ clientId:"", carrier:"", amount:"", memberId:"", month, policyType:"aca" });
  const months = useMemo(() => {
    const gen = MONTHS_LIST();
    const dataMonths = [...new Set((commissions||[]).map(r=>r.month).filter(Boolean))];
    const all = [...new Set([...gen, ...dataMonths])].sort().reverse();
    return all;
  }, [commissions]);
  const COMM_TIPOS_FIJOS = new Set(["aca","medicare","life","auto","home","renters","commercial","dental","vision"]);
  const COMM_DENTRO_DE_MEDICARE = new Set(["ma","medsup","partd"]);
  const COMM_POLICY_TYPES = [
    {id:"all",        label:"Todas",     labelEn:"All",        icon:"📊", color:"#00d4ff"},
    {id:"aca",        label:"Salud",     labelEn:"Health",     icon:"🏥", color:"#06d6a0"},
    {id:"medicare",   label:"Medicare",  labelEn:"Medicare",   icon:"💊", color:"#a78bfa"},
    {id:"life",       label:"Vida",      labelEn:"Life",       icon:"❤️", color:"#f43f5e"},
    {id:"auto",       label:"Auto",      labelEn:"Auto",       icon:"🚗", color:"#3b82f6"},
    {id:"home",       label:"Hogar",     labelEn:"Home",       icon:"🏠", color:"#f59e0b"},
    {id:"renters",    label:"Renters",   labelEn:"Renters",    icon:"🏘️", color:"#f97316"},
    {id:"commercial", label:"Comercial", labelEn:"Commercial", icon:"🏢", color:"#8b5cf6"},
    {id:"dental",     label:"Dental",    labelEn:"Dental",     icon:"🦷", color:"#22d3ee"},
    {id:"vision",     label:"Visión",    labelEn:"Vision",     icon:"👁️", color:"#10b981"},
    // El resto de los tipos de fábrica (Lissette 2026-10-19): Gastos Finales,
    // Accidentes, Hospitalización, Mascotas, Viaje... Medicare Advantage,
    // Suplementario y Parte D NO se añaden sueltos: aquí van juntos en
    // "Medicare" (id "medicare"), que es como se suben los estados de cuenta.
    ...policyTypes.filter(p=>!p.custom && !COMM_TIPOS_FIJOS.has(p.id) && !COMM_DENTRO_DE_MEDICARE.has(p.id)).map(p=>({
      id: p.id,
      label: p.labelEs || p.label,
      labelEn: p.label || p.labelEs,
      icon: p.icon,
      color: p.color || "#64748b",
    })),
    // Merge user-created custom policy types so Commissions filters show them
    ...policyTypes.filter(p=>p.custom).map(p=>({
      id: p.id,
      label: p.labelEs || p.label,
      labelEn: p.label || p.labelEs,
      icon: p.icon,
      color: p.color || "#8b5cf6",
    })),
  ];
  const availableYears = useMemo(() => {
    const yrs = [...new Set((commissions||[]).map(r=>r.month?.substring(0,4)).filter(Boolean))];
    const cur = String(new Date().getFullYear());
    if (!yrs.includes(cur)) yrs.push(cur);
    return yrs.sort().reverse();
  }, [commissions]);
  const normalizedComms = (commissions||[]).map(r=>({...r, policyType: r.policyType||"aca"}));
  // LIVE name resolution: a commission belongs to a client if it has a
  // valid stored clientId OR its raw file name now matches a client by
  // first+last. This means creating the client in the CRM later makes
  // its "Not in CRM" commissions move to Paid automatically — no
  // re-upload, no manual Assign. Memoized on clients+commissions so it
  // only recomputes when those change, not on every filter toggle.
  // See the module-level helpers above — shared with ReportsView's Reclamos tab.
  //
  // Va ANTES de monthComms a propósito: el filtro por estado necesita saber a
  // qué cliente pertenece cada comisión para poder caer en el estado de la
  // ficha cuando el statement no trae columna de estado (ver commState).
  const nameAliasMap = useMemo(() => buildNameAliasMap(commissions, clients), [commissions, clients]);
  const resolvedClientIdByComm = useMemo(
    () => buildResolvedClientIdMap(commissions, clients, nameAliasMap),
    [clients, commissions, nameAliasMap],
  );
  const resolvedClientId = (r) => resolvedClientIdByComm.get(commKey(r)) || null;
  // Índice por id para poder pasar de "esta comisión es de X" a la ficha de X.
  const clientById = useMemo(() => new Map((clients || []).map((c) => [c.id, c])), [clients]);
  const commissionClientIds = useMemo(
    () => clientIdsWithPaidCommission(commissions, resolvedClientIdByComm),
    [commissions, resolvedClientIdByComm],
  );
  // Estado de cada cliente, normalizado a mayúsculas, para poder cruzarlo con
  // el de las comisiones sin depender de cómo venga escrito.
  const clientStateById = useMemo(() => {
    const m = new Map();
    (clients || []).forEach(c => {
      const s = String(c.state || c.addressState || "").trim().toUpperCase();
      if (s) m.set(c.id, s);
    });
    return m;
  }, [clients]);
  // Estado EFECTIVO de una comisión. El statement manda cuando trae columna de
  // estado; si no la trae —y la mayoría de las compañías no la traen— se usa el
  // estado de la ficha del cliente al que la comisión resuelve.
  //
  // Sin este respaldo, elegir "FL" vaciaba medio informe: las filas sin estado
  // no coincidían con NINGÚN estado y se caían del filtro aunque el cliente
  // fuera de Florida (Lissette 2026-08-14: "el filtro de estado en comisiones
  // no está funcionando bien").
  const commState = (r) => {
    const own = String(r.state || "").trim().toUpperCase();
    if (own) return own;
    const cid = resolvedClientId(r);
    return (cid && clientStateById.get(cid)) || "";
  };
  const yearMatch = (c) => {
    if (month !== "all") return c.month === month;
    if (yearFilter === "all") return true;
    return !c.month || c.month.startsWith(yearFilter);
  };
  const monthComms = normalizedComms.filter(c => {
    if(!yearMatch(c)) return false;
    if(policyTypeFilter !== "all" && c.policyType !== policyTypeFilter) return false;
    if(!carrierMatch(c)) return false;
    if(!stateMatch(c)) return false;
    return true;
  });
  const monthCarriers = useMemo(() => [...new Set(normalizedComms.filter(c=>yearMatch(c)).map(c=>c.carrier).filter(Boolean))].sort(), [normalizedComms, month, yearFilter]);
  // La lista de estados que ofrece el filtro se arma con el MISMO criterio con
  // el que después se filtra (commState), no solo con la columna del archivo.
  // Si no, un estado que solo existe en las fichas de los clientes nunca
  // aparecía como opción.
  const monthStates = useMemo(
    () => [...new Set(normalizedComms.filter(c=>yearMatch(c)).map(c=>commState(c)).filter(Boolean))].sort(),
    [normalizedComms, month, yearFilter, clientStateById, resolvedClientIdByComm],
  );
  const trackerRows = useMemo(() => {
    return clients.map(c => {
      const rec = monthComms.find(r => resolvedClientId(r) === c.id);
      return { client: c, record: rec || null };
    }).sort((a,b) => `${a.client.firstName||""} ${a.client.lastName||""}`.trim()
      .localeCompare(`${b.client.firstName||""} ${b.client.lastName||""}`.trim(), undefined, { sensitivity:"base" }));
  }, [clients, monthComms, resolvedClientIdByComm]);
  // Client-name search + active/inactive narrowing for the tracker lists.
  const trackerNameMatch = (c) => {
    const q = trackerSearch.trim().toLowerCase();
    if (!q) return true;
    const a = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
    const b = `${c.lastName || ""} ${c.firstName || ""}`.toLowerCase();
    return a.includes(q) || b.includes(q);
  };
  const trackerStatusMatch = (c) =>
    trackerStatus === "all" ? true
      : trackerStatus === "inactive" ? c.status === "inactive"
        : c.status !== "inactive";
  // El listado del seguimiento se arma a partir de CLIENTES, no de comisiones,
  // así que el filtro por estado tenía que aplicarse aquí también. Antes solo
  // se filtraban las comisiones: al elegir "FL" seguían saliendo todos los
  // clientes de los demás estados, y como su comisión sí quedaba fuera del
  // filtro aparecían en la lista de "no pagó" — leyéndose como si la compañía
  // les debiera dinero (Lissette 2026-08-14).
  //
  // Se acepta al cliente si el estado de su ficha coincide, y si la ficha no
  // tiene estado se admite cuando su comisión del período sí encaja (r.record
  // ya pasó por el mismo filtro).
  const trackerStateMatch = (r) => {
    if (!stateSel.length) return true;
    const cs = String(r.client.state || r.client.addressState || "").trim().toUpperCase();
    if (cs) return stateSel.includes(cs);
    return !!r.record;
  };
  const visibleTrackerRows = trackerRows.filter(r => trackerNameMatch(r.client) && trackerStatusMatch(r.client) && trackerStateMatch(r));
  // Cuántos clientes quedaron fuera SOLO por no tener estado en la ficha. Se
  // enseña bajo la lista: un filtro que esconde gente sin decirlo es justo lo
  // que hacía dudar de este módulo.
  const hiddenNoState = stateSel.length
    ? trackerRows.filter(r => trackerNameMatch(r.client) && trackerStatusMatch(r.client)
        && !String(r.client.state || r.client.addressState || "").trim() && !r.record).length
    : 0;
  const paidRows    = visibleTrackerRows.filter(r => r.record && r.client.status !== "inactive");
  // Prospectos y referidos no tienen póliza: no se les espera comisión, así que
  // no van a "no pagó" (ticket de Juan Carlos Salazar, ver cuentaEnComisiones.js).
  const missingRows = visibleTrackerRows.filter(r => !r.record && esperaComision(r.client));
  const inactivePaid = visibleTrackerRows.filter(r => r.record && r.client.status === "inactive");
  const totalReceived = monthComms.filter(c=>c.amount>0).reduce((s,c) => s+parseFloat(c.amount||0), 0);
  const chargebacks = normalizedComms.filter(c => yearMatch(c) && c.amount < 0 && carrierMatch(c) && stateMatch(c));
  const chargebackTotal = chargebacks.reduce((s,r) => s + parseFloat(r.amount||0), 0);
  const notInCRM = useMemo(() => {
    return commissions.filter(r => {
      const ym = month !== "all" ? r.month === month : (yearFilter === "all" ? true : (!r.month || r.month.startsWith(yearFilter)));
      if (!ym) return false;
      if (!carrierMatch(r)) return false;
      if (!stateMatch(r)) return false;
      // Truly "Not in CRM" only if it resolves to no client — either by
      // stored clientId OR by a live first+last name match. Add the
      // client to the CRM and it leaves this list automatically.
      return !resolvedClientId(r);
    }).sort((a,b)=>(a.clientNameRaw||a.name||"").localeCompare(b.clientNameRaw||b.name||"", undefined, { sensitivity:"base" }));
  }, [commissions, month, clients, yearFilter, carrierSel, stateSel, resolvedClientIdByComm]);
  const notInCRMTotal = notInCRM.reduce((s,r) => s+parseFloat(r.amount||0), 0);
  const inactivePaidTotal = inactivePaid.reduce((s,r) => s+parseFloat(r.record?.amount||0), 0);
  // ── Payment report (print / PDF + CSV), honoring the active filters ──
  const reportRows = useMemo(() => {
    return monthComms.map(r => {
      const cid = resolvedClientId(r);
      const c = cid ? clients.find(x => x.id === cid) : null;
      const name = c
        ? `${c.lastName || ""}, ${c.firstName || ""}`.replace(/^,\s*|,\s*$/g, "").trim() || (r.clientNameRaw || r.name || "—")
        : (r.clientNameRaw || r.name || "—");
      return {
        month: r.month || "", name, carrier: r.carrier || "", state: commState(r),
        saleType: r.saleType || "", memberId: r.memberIdRaw || "",
        amount: parseFloat(r.amount || 0), inCrm: !!c,
      };
    }).sort((a, b) => (a.month || "").localeCompare(b.month || "") || a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [monthComms, clients, resolvedClientIdByComm]);
  const reportScopeLabel = () => {
    const parts = [];
    parts.push(month !== "all" ? fmtMonth(month) : (yearFilter === "all" ? (lang === "es" ? "Todos los años" : "All years") : yearFilter));
    if (carrierSel.length) parts.push(carrierSel.join(", "));
    if (stateSel.length) parts.push(stateSel.join(", "));
    return parts.join("  ·  ");
  };
  const printPaymentReport = () => {
    const rows = reportRows;
    const total = rows.reduce((s, r) => s + r.amount, 0);
    const esc = s => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const byMonth = {};
    rows.forEach(r => { (byMonth[r.month || "—"] = byMonth[r.month || "—"] || []).push(r); });
    const monthBlocks = Object.keys(byMonth).sort().map(m => {
      const list = byMonth[m];
      const mt = list.reduce((s, r) => s + r.amount, 0);
      const trs = list.map(r => `<tr><td>${esc(r.name)}${r.inCrm ? "" : ' <span class="nc">(No en CRM)</span>'}</td><td>${esc(r.carrier)}</td><td>${esc(r.state)}</td><td>${esc(r.memberId)}</td><td class="amt">${esc(fmtMoney(r.amount))}</td></tr>`).join("");
      return `<h2>${esc(m === "—" ? (lang === "es" ? "Sin mes" : "No month") : fmtMonth(m))} &mdash; <span class="amt">${esc(fmtMoney(mt))}</span> &middot; ${list.length} ${lang === "es" ? "pagos" : "payments"}</h2>
        <table><thead><tr><th>${lang === "es" ? "Cliente" : "Client"}</th><th>${lang === "es" ? "Compañía" : "Carrier"}</th><th>${lang === "es" ? "Estado" : "State"}</th><th>Member ID</th><th class="amt">${lang === "es" ? "Monto" : "Amount"}</th></tr></thead><tbody>${trs}</tbody></table>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${lang === "es" ? "Reporte de Pagos" : "Payment Report"}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;padding:24px;}h1{font-size:20px;margin:0 0 4px;}.sub{color:#64748b;font-size:12px;margin-bottom:16px;}h2{font-size:14px;margin:20px 0 6px;border-bottom:2px solid #e2e8f0;padding-bottom:4px;}table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:8px;}th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0;}th{background:#f1f5f9;}.amt{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap;}.nc{color:#b45309;font-size:10px;}.grand{margin-top:20px;font-size:16px;font-weight:bold;text-align:right;border-top:2px solid #0f172a;padding-top:8px;}@media print{body{padding:0;}}</style></head>
      <body><h1>${lang === "es" ? "Reporte de Pagos" : "Payment Report"}</h1>
      <div class="sub">${esc(reportScopeLabel())} &middot; ${rows.length} ${lang === "es" ? "registros" : "records"} &middot; ${esc(new Date().toLocaleDateString("en-US"))}</div>
      ${monthBlocks || `<p>${lang === "es" ? "No hay pagos para este filtro." : "No payments for this filter."}</p>`}
      <div class="grand">${lang === "es" ? "Total" : "Total"}: ${esc(fmtMoney(total))}</div>
      <script>window.onload=function(){window.print();};</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error(lang === "es" ? "Permite ventanas emergentes para imprimir" : "Allow pop-ups to print"); return; }
    w.document.write(html); w.document.close();
  };
  const exportPaymentCsv = () => {
    const rows = reportRows;
    if (!rows.length) { toast.error(lang === "es" ? "No hay pagos para exportar" : "No payments to export"); return; }
    const head = [lang === "es" ? "Mes" : "Month", lang === "es" ? "Cliente" : "Client", lang === "es" ? "Compañía" : "Carrier", lang === "es" ? "Estado" : "State", "Member ID", lang === "es" ? "Tipo" : "Type", lang === "es" ? "En CRM" : "In CRM", lang === "es" ? "Monto" : "Amount"];
    const q = v => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const lines = [head.map(q).join(",")];
    rows.forEach(r => lines.push([r.month ? fmtMonth(r.month) : "", r.name, r.carrier, r.state, r.memberId, r.saleType, r.inCrm ? (lang === "es" ? "Sí" : "Yes") : "No", r.amount.toFixed(2)].map(q).join(",")));
    const total = rows.reduce((s, r) => s + r.amount, 0);
    lines.push([q(lang === "es" ? "TOTAL" : "TOTAL"), "", "", "", "", "", "", q(total.toFixed(2))].join(","));
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const scope = month !== "all" ? month : (yearFilter === "all" ? "todos" : yearFilter);
    a.href = url; a.download = `pagos-${scope}.csv`; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const [timelineSearch, setTimelineSearch] = useState("");
  const [timelineCarrierSel, setTimelineCarrierSel] = useState([]); // [] = all
  const [timelineStateSel, setTimelineStateSel] = useState([]);     // [] = all
  const timelineScrollRef = useRef(null);
  const scrollTimeline = (dx) => timelineScrollRef.current?.scrollBy({ left: dx, behavior: "smooth" });
  const allCarriersTimeline = useMemo(() => [...new Set(commissions.map(r => r.carrier).filter(Boolean))].sort(), [commissions]);
  const allStatesTimeline = useMemo(() => [...new Set(commissions.map(r => commState(r)).filter(Boolean))].sort(), [commissions, clientStateById, resolvedClientIdByComm]);
  // Group EVERY commission for the timeline, not just rows with a stored
  // clientId. Uses the live resolver so name-matched rows show too, INCLUDES
  // inactive clients (tagged), and buckets rows that resolve to nobody under
  // a "No en CRM" pseudo-row so nothing is hidden (Lissette 2026-07-01).
  const timelineRows = useMemo(() => {
    const clientById = new Map(clients.map(c => [c.id, c]));
    const groups = new Map();
    for (const r of (commissions || [])) {
      const cid = resolvedClientId(r);
      const client = cid ? clientById.get(cid) : null;
      const key = client ? `c:${client.id}` : `n:${_normName(r.clientNameRaw || r.name || "")}`;
      if (!client && !_normName(r.clientNameRaw || r.name || "")) continue;
      if (!groups.has(key)) {
        groups.set(key, {
          client: client || null,
          notInCrm: !client,
          rawName: client ? "" : (r.clientNameRaw || r.name || ""),
          rows: [], carriers: new Set(), states: new Set(),
        });
      }
      const g = groups.get(key);
      g.rows.push(r);
      if (r.carrier) g.carriers.add(r.carrier);
      { const st = commState(r); if (st) g.states.add(st); }
    }
    return [...groups.values()]
      .map(g => ({ ...g, carriers: [...g.carriers], states: [...g.states] }))
      .filter(g => {
        const name = g.client ? `${g.client.firstName || ""} ${g.client.lastName || ""}` : g.rawName;
        if (timelineSearch && !name.toLowerCase().includes(timelineSearch.toLowerCase())) return false;
        if (timelineCarrierSel.length && !g.carriers.some(c => timelineCarrierSel.includes(c))) return false;
        if (timelineStateSel.length && !g.states.some(s => timelineStateSel.includes(s))) return false;
        return true;
      })
      .sort((a, b) => {
        const an = a.client ? (a.client.lastName || a.client.firstName || "") : (a.rawName || "");
        const bn = b.client ? (b.client.lastName || b.client.firstName || "") : (b.rawName || "");
        return an.localeCompare(bn, undefined, { sensitivity: "base" });
      });
  }, [commissions, clients, timelineSearch, timelineCarrierSel, timelineStateSel, resolvedClientIdByComm]);
  const timelineMonths = useMemo(
    () => [...new Set(timelineRows.flatMap(g => g.rows.map(r => r.month)).filter(Boolean))].sort(),
    [timelineRows],
  );
  const [commFiles, setCommFiles] = useState([]); // [{name, records}]
  const [autoResults, setAutoResults] = useState(null); // matched results after auto-run
  const [isRunning, setIsRunning] = useState(false);
  const [parseError, setParseError] = useState("");
  const parseExcelDate = (val) => {
    if (!val || val === "nan" || val === "") return null;
    if (val instanceof Date && !isNaN(val)) return val;
    // Excel serial date. `(val-25569)*86400000` is a UTC-midnight instant;
    // reading it back with LOCAL getters (or toLocaleDateString) in a
    // negative-UTC zone (Florida = UTC-4/-5) shifts it back a day, which
    // can misfile a commission into the prior month. Rebuild at LOCAL
    // midnight from the UTC calendar components (feedback_local_day_not_utc).
    if (typeof val === "number" && val > 40000 && val < 60000) {
      const u = new Date((val - 25569) * 86400000);
      return new Date(u.getUTCFullYear(), u.getUTCMonth(), u.getUTCDate());
    }
    const s = String(val).trim(); if (!s) return null;
    if (s.match(/^\d{4}-\d{2}-\d{2}/)) { const p = s.split(/[-T ]/); return new Date(parseInt(p[0]),parseInt(p[1])-1,parseInt(p[2])); }
    const mdy = s.match(/^(\w{3})-(\d{2})-(\d{2})$/);
    if (mdy) { const mo = {Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11}[mdy[1]]; if (mo!==undefined) return new Date(parseInt(mdy[3])+2000,mo,parseInt(mdy[2])); }
    const sl = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (sl) { let yr=parseInt(sl[3]); if(yr<100) yr+=2000; return new Date(yr,parseInt(sl[1])-1,parseInt(sl[2])); }
    const d = new Date(s); if (!isNaN(d) && d.getFullYear()>2000) return d; return null;
  };
  // Hardcoded positional parser for known carrier Excel formats (AgentCommissions)
  const parseCommissionsPositional = (data) => {
    const records = []; let currentIssuer = "";
    for (let i = 0; i < data.length; i++) {
      const row = data[i]; if (!row || row.length < 10) continue;
      const c1=String(row[1]||"").trim(), c2=String(row[2]||"").trim(), c3=String(row[3]||"").trim();
      if (c1==="Issuer"||c2==="Total"||!c3) continue;
      if (c1&&c1!=="Total") currentIssuer=c1;
      let comm=0; try{comm=parseFloat(row[4])||0;}catch(e){}
      if (!comm) continue;
      let dueDate="",dueMonth="";
      const dueParsed=parseExcelDate(row[14]);
      if(dueParsed){dueDate=dueParsed.toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});dueMonth=dueParsed.toLocaleDateString("en-US",{year:"numeric",month:"long"});}
      records.push({issuer:currentIssuer||"",name:c3,commission:comm,policy:String(row[5]||"").trim(),state:String(row[6]||"").trim(),source:String(row[7]||"").trim(),saleType:String(row[8]||"").trim(),dueDate,dueMonth});
    }
    return records;
  };
  const parseCommissionsFlex = (data) => {
    if (!data || data.length < 2) return [];
    let hIdx = 0, hScore = 0;
    for (let r = 0; r < Math.min(25, data.length); r++) {
      let sc = 0;
      (data[r]||[]).forEach(v => { const s=String(v||"").trim().toLowerCase(); if(s.length>=2&&s.length<=50&&!/^[$\d(]/.test(s)) sc++; });
      if (sc > hScore && (data[r]||[]).length >= 2) { hScore = sc; hIdx = r; }
    }
    const headers = (data[hIdx]||[]).map(h => String(h||"").trim().toLowerCase());
    const nameAliases = ["consumer_name","consumer name","customer name","client name","customer","client","name","insured","member","subscriber","enrollee","policyholder","beneficiary","full name","member name","nombre completo","nombre del cliente","agent_client_name","agent client name"];
    const amtAliases  = ["commission_paid","commission paid","comm","commission","amount","premium","comm amt","commission amount","net commission","payout","payment","total commission","net amount","monto","comision","gross commission","advance amount","earned","net pay","commission due"];
    const issuerAliases = ["provider_name","provider name","provider","issuer","carrier","insurance company","carrier name","payor","plan name","aseguradora","company"];
    const policyAliases = ["policy_number","policy number","policy","policy no","poliza","member id","contract","enrollment id","policy #","memberid","member_id"];
    const dateAliases = ["due","due date","due_date","effective","effective date","fecha","coverage month","stmntdate","statement date","payment date","fecha de pago","effective_date","eff date"];
    const typeAliases  = ["sale_type","sale type","new/renewal","transaction type","type","enrollment type","tipo de venta","trans type"];
    const stateAliases = ["state","state_name","estado","resident state","st"];
    const sourceAliases = ["sale_source","sale source","source","lead source","channel","fuente","writing agent","downline","agent"];
    // Product / line-of-business column (e.g. Florida Blue "prod" = health|dental).
    // Kept BEFORE the generic "type" so a "coverage type"/"product type" header
    // is read as the line, not as the sale type.
    const prodAliases = ["prod","product","product_type","product type","product line","coverage","coverage type","coverage_type","plan type","plan_type","line of business","lob","producto","tipo de producto","tipo de plan","ramo","plan"];
    const findCol = (aliases) => {
      for (const a of aliases) { const i=headers.indexOf(a); if(i>=0) return i; }
      for (const a of aliases) { const i=headers.findIndex(h=>h.includes(a)||a.includes(h)); if(i>=0) return i; }
      return -1;
    };
    const prodCol = findCol(prodAliases);
    const ci = { name:findCol(nameAliases), amt:findCol(amtAliases), issuer:findCol(issuerAliases), policy:findCol(policyAliases), date:findCol(dateAliases), type:findCol(typeAliases), state:findCol(stateAliases), source:findCol(sourceAliases), prod:prodCol };
    if (ci.name<0 && ci.amt<0) {
      headers.forEach((h,i) => {
        if (ci.name<0 && data.slice(hIdx+1,hIdx+5).some(r=>String(r[i]||"").trim().length>3&&isNaN(String(r[i]||"").replace(/[$,]/g,"")))) ci.name=i;
      });
      headers.forEach((h,i) => {
        if (ci.amt<0 && i!==ci.name && data.slice(hIdx+1,hIdx+5).some(r=>!isNaN(parseFloat(String(r[i]||"").replace(/[$,()]/g,""))))) ci.amt=i;
      });
    }
    if (ci.name<0) return [];
    const records = []; let currentIssuer = "";
    for (let i = hIdx+1; i < data.length; i++) {
      const row = data[i]; if (!row||row.length<2) continue;
      const name = String(row[ci.name]||"").trim();
      if (!name||name.toLowerCase()==="total"||name.toLowerCase()==="grand total") continue;
      let comm = 0;
      if (ci.amt>=0) { let raw=String(row[ci.amt]||"").trim(); const neg=raw.includes("(")&&raw.includes(")"); raw=raw.replace(/[$,()]/g,""); comm=parseFloat(raw)||0; if(neg) comm=-comm; }
      if (!comm && ci.amt>=0 && !String(row[ci.amt]||"").trim()) continue;
      const issuer = ci.issuer>=0 ? String(row[ci.issuer]||"").trim() : "";
      if (issuer&&issuer.toLowerCase()!=="total") currentIssuer=issuer;
      let dueDate="", dueMonth="";
      if (ci.date>=0) { const dp=parseExcelDate(row[ci.date]); if(dp){ dueDate=dp.toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}); dueMonth=dp.toLocaleDateString("en-US",{year:"numeric",month:"long"}); } }
      records.push({ issuer:currentIssuer||issuer||"", name, commission:comm, policy:ci.policy>=0?String(row[ci.policy]||"").trim():"", saleType:ci.type>=0?String(row[ci.type]||"").trim():"", product:ci.prod>=0?String(row[ci.prod]||"").trim():"", state:ci.state>=0?String(row[ci.state]||"").trim():"", source:ci.source>=0?String(row[ci.source]||"").trim():"", dueDate, dueMonth });
    }
    return records;
  };
  const parseRawFromText = (text, sep) => {
    const lines = text.trim().split("\n").filter(l=>l.trim());
    return lines.map(l => l.split(sep).map(v => v.replace(/^"|"$/g,"").trim()));
  };
  // Dedicated parser for carrier-statement PDFs (Acosta/Medicare,
  // Kaiser, …). PDFs extract jumbled and the generic flex parser grabbed
  // the "Client Name" HEADER as a client and missed every real row. This
  // works on the reconstructed line text (robust to column shifts):
  // pull the client name, carrier, coverage type and commission per row.
  const parseCommissionsPdfLines = (rawLines, fechaDelPago = null) => {
    const COVER = /\b(MAPD|Medicare\s*Advantage|Medicare|PDP|D-?SNP|HMO[- ]?POS|HMO|PPO|EPO|ACA|Health|Dental|Vision|Life|Final\s*Expense)\b/i;
    // Statements print the writing agent's own name ("Apellido, Nombre") above
    // their book; a bare short line like that must not be mistaken for a
    // carrier. Built from the agency roster instead of a hardcoded surname —
    // the old literal shipped a real person's name inside every JS bundle
    // (public demo included) and only worked for one agency.
    const ownNameTokens = new Set(
      [...(users || []), currentUser].filter(Boolean)
        .flatMap(u => String(u.name || "").toLowerCase().split(/\s+/))
        .filter(w => w.length >= 2)
    );
    const isAgentNameLine = (l) => {
      const comma = l.indexOf(",");
      if (comma <= 0) return false;
      const words = l.slice(0, comma).toLowerCase().split(/\s+/).filter(Boolean);
      return words.length > 0 && words.every(w => ownNameTokens.has(w));
    };
    const MONEY = /-?\$?\s?[\d,]+\.\d{2}/g;
    const norm = s => String(s||"").replace(/\t/g," ").replace(/\s+/g," ").trim();
    const lines = (rawLines||[]).map(norm).filter(Boolean);
    let carrier = "";
    let grandTotal = null;
    for (const l of lines) { const m=l.match(/\(([^)]{2,40})\)\s*$/); if(m && /kaiser|humana|cigna|aetna|ambetter|oscar|united|molina|blue|wellcare|devoted/i.test(m[1])){ carrier=m[1].trim(); break; } }
    const isHeader = l => /\b(client name|member name|agent name)\b/i.test(l) && /\b(commission|premium|due date)\b/i.test(l);
    const isTotal  = l => /total\s+commission\s+earned/i.test(l) || /^totals?\s+for\b/i.test(l) || /^grand\s+total/i.test(l);
    const isJunk   = l => /commission report|advisor llc|^page\b|^\d{1,3}$|^\d{2}\/\d{2}\/\d{4}$/i.test(l);
    const recs = [];
    for (const l of lines) {
      if (isTotal(l)) {
        const mt = l.match(/total\s+commission\s+earned[^$]*\$?\s*(-?[\d,]+\.\d{2})/i);
        if (mt) grandTotal = parseFloat(mt[1].replace(/[,$]/g,""));
        const mc = l.match(/totals?\s+for\s+carrier:\s*([^$]+?)(?:\s{2,}|\s+coverage type|\s*$)/i);
        if (mc) carrier = mc[1].trim();
        continue;
      }
      if (isHeader(l) || isJunk(l)) continue;
      const moneys = l.match(MONEY);
      if (!moneys || !moneys.length) {
        if (l.length<=40 && /^[A-Za-z][A-Za-z .,&'\-]+$/.test(l) && !COVER.test(l) && !isAgentNameLine(l)) carrier = l.trim();
        continue;
      }
      const commission = parseFloat(moneys[moneys.length-1].replace(/[,$\s]/g,"")) || 0;
      let name = "";
      let m = l.match(/^(.+?)\s+[A-Za-z0-9]{5,}\s+\d{4}-\d{2}-\d{2}\b/);              // Acosta: name policy ISO-date
      if (m) name = m[1];
      if (!name) { m = l.match(/^(.+?)\s+[A-Z]{2}\s+\d{1,2}\/\d{1,2}\/\d{2,4}\b/); if (m) name = m[1]; } // Kaiser: name ST mm/dd/yyyy
      if (!name) name = l.split(MONEY)[0].replace(/\s+[A-Za-z0-9]{5,}\s+\d{4}-\d{2}-\d{2}.*$/,"").replace(/\s+[A-Z]{2}\s+\d{1,2}\/\d{1,2}\/\d{2,4}.*$/,"").trim();
      name = name.trim();
      const low = name.toLowerCase();
      if (!name || /^(client name|member name|agent name|coverage type|comm period|premium|commission|totals?|policy)\b/.test(low)) continue;
      // Capture the row date (ISO 2025-11-01 for Acosta, mm/dd/yyyy for
      // Kaiser) so the commission buckets into the right month/year
      // instead of falling back to the global filter.
      let dueDate = "", dueMonth = "";
      // La fecha ISO de una fila Acosta es la de EFECTIVIDAD de la póliza, no la
      // del pago: si el estado de cuenta trae su fecha (arriba o en el nombre
      // del archivo), esa manda (Wendy Cercio, 2026-10-28, E-401).
      const esFechaDeEfectividad = /\b\d{4}-\d{2}-\d{2}\b/.test(l);
      const dm = (fechaDelPago && esFechaDeEfectividad) ? null : (l.match(/\b\d{4}-\d{2}-\d{2}\b/) || l.match(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/));
      if (fechaDelPago && esFechaDeEfectividad) { dueDate = fechaDelPago.toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}); dueMonth = fechaDelPago.toLocaleDateString("en-US",{year:"numeric",month:"long"}); }
      if (dm) { const dp = parseExcelDate(dm[0]); if (dp) { dueDate = dp.toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}); dueMonth = dp.toLocaleDateString("en-US",{year:"numeric",month:"long"}); } }
      recs.push({ issuer: carrier || "", name, commission, policy:"", saleType:(l.match(COVER)||[])[0]||"", state:"", source:"", dueDate, dueMonth });
    }
    // Kaiser repeats the agent name in front of every member — strip the
    // common leading token run (only if it's clearly a shared prefix).
    if (recs.length > 1) {
      const sp = recs.map(r => r.name.split(" "));
      let k = 0;
      while (sp.every(a => a[k] !== undefined && a[k] === sp[0][k]) && k < sp[0].length - 1) k++;
      if (k >= 2) recs.forEach((r,i)=>{ const rest = sp[i].slice(k).join(" ").trim(); if (rest) r.name = rest; });
    }
    return { records: recs, grandTotal };
  };
  const processFile = (file) => {
    setParseError("");
    const ext = file.name.split(".").pop().toLowerCase();
    const addRecords = (records) => {
      if (!records.length) { setParseError(`⚠️ ${file.name}: No se encontraron registros. Verifica que el archivo tenga datos de comisiones.`); return; }
      setCommFiles(prev => {
        const exists = prev.find(f=>f.name===file.name);
        if (exists) return prev;
        return [...prev, { name:file.name, ext, records }];
      });
    };
    if (ext==="csv"||ext==="txt") {
      file.text().then(text => {
        const firstLine=text.trim().split("\n")[0];
        const sep=firstLine.includes("\t")?"\t":firstLine.includes(";")?";":",";
        const raw = parseRawFromText(text, sep);
        const flexResult = parseCommissionsFlex(raw);
        const posResult = parseCommissionsPositional(raw);
        addRecords(posResult.length > flexResult.length ? posResult : flexResult);
      });
    } else if (ext==="xlsx"||ext==="xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (typeof XLSX==="undefined") { setParseError("Librería Excel no disponible - exporta el archivo como CSV e inténtalo de nuevo."); return; }
        const wb = XLSX.read(e.target.result,{type:"array",cellDates:true});
        let allRecords = [];
        wb.SheetNames.forEach(sn => {
          const ws = wb.Sheets[sn];
          const rows = XLSX.utils.sheet_to_json(ws,{header:1,defval:""});
          if (rows.length >= 2) {
            const flexResult = parseCommissionsFlex(rows);
            const posResult = parseCommissionsPositional(rows);
            // Use whichever parser found more records
            const best = posResult.length > flexResult.length ? posResult : flexResult;
            allRecords = allRecords.concat(best);
          }
        });
        addRecords(allRecords);
      };
      reader.readAsArrayBuffer(file);
    } else if (ext==="pdf") {
      if (typeof pdfjsLib==="undefined") { setParseError("PDF.js no está disponible. Convierte tu PDF a Excel en ilovepdf.com e intenta de nuevo."); return; }
      file.arrayBuffer().then(async (ab) => {
        try {
          const pdf = await pdfjsLib.getDocument({data:new Uint8Array(ab)}).promise;
          const allText = [];
          for (let pg=1;pg<=pdf.numPages;pg++) {
            const page=await pdf.getPage(pg); const tc=await page.getTextContent();
            let prevY=null,line="";
            tc.items.forEach(item=>{if(prevY!==null&&Math.abs(item.transform[5]-prevY)>5){allText.push(line);line="";}if(line&&item.str.trim())line+="\t";line+=item.str;prevY=item.transform[5];});
            if(line)allText.push(line);
          }
          const lines=allText.join("\n").split("\n").filter(l=>l.trim());
          const rows=[]; lines.forEach(ln=>{const cells=ln.split(/\t/);if(cells.length>=2)rows.push(cells.map(c=>c.trim()));else{const cells2=ln.split(/\s{3,}/);if(cells2.length>=2)rows.push(cells2.map(c=>c.trim()));}});
          if(rows.length<2){setParseError(lang==="es"?"No se encontró una tabla en el PDF. Conviértelo a Excel/CSV en ilovepdf.com y súbelo así.":"No table found in the PDF. Convert it to Excel/CSV at ilovepdf.com and upload that.");return;}
          // Dedicated PDF parser first (line-text based, robust to the
          // column jumble that made flex grab the "Client Name" header
          // as a client). Fall back to flex only if it finds nothing.
          const pdfParsed = parseCommissionsPdfLines(lines, fechaDelEstadoDeCuenta(lines, file.name));
          const pdfRecs = pdfParsed.records.length ? pdfParsed.records : parseCommissionsFlex(rows);
          // RECONCILIATION GUARD — PDFs extract unreliably (carrier
          // statements vary wildly: Kaiser, Acosta/Medicare, etc. and the
          // text comes out jumbled). If the file states a grand total and
          // what we parsed doesn't match it, do NOT silently import a
          // wrong number — tell her to use the bulletproof Excel path.
          const joined = lines.join(" ").replace(/\s+/g," ");
          const mTot = joined.match(/total\s+commission\s+earned[^$]*\$?\s*(-?[\d,]+\.\d{2})/i);
          const stated = pdfParsed.grandTotal != null
            ? pdfParsed.grandTotal
            : (mTot ? parseFloat(mTot[1].replace(/,/g,"")) : null);
          const parsedSum = pdfRecs.reduce((s,r)=>s+(Number(r.commission)||0),0);
          if (!pdfRecs.length) {
            setParseError(lang==="es"
              ? "No pude leer comisiones de este PDF (el formato del statement se extrae mal). Conviértelo a Excel/CSV en ilovepdf.com y súbelo así — esa vía cuadra exacto."
              : "Couldn't read commissions from this PDF (this statement layout extracts badly). Convert it to Excel/CSV at ilovepdf.com and upload that — that path reconciles exactly.");
            return;
          }
          addRecords(pdfRecs);
          if (stated != null && Math.abs(parsedSum - stated) > 0.01) {
            setParseError(lang==="es"
              ? `⚠️ El PDF NO cuadró: leí ${fmtMoney(parsedSum)} pero el statement dice ${fmtMoney(stated)}. Los PDF se leen mal a veces. Revisa abajo, o (recomendado) convierte este PDF a Excel/CSV en ilovepdf.com y súbelo así para que cuadre exacto.`
              : `⚠️ PDF did NOT reconcile: parsed ${fmtMoney(parsedSum)} but the statement says ${fmtMoney(stated)}. PDFs extract unreliably. Review below, or (recommended) convert this PDF to Excel/CSV at ilovepdf.com and upload that for an exact match.`);
          }
        } catch(err) { setParseError("Error leyendo PDF: "+err.message+". Prueba ilovepdf.com para convertirlo."); }
      });
    } else { setParseError("Formato no reconocido. Usa .xlsx, .xls, .csv, o .pdf"); }
  };
  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files || e.target?.files;
    if (files) Array.from(files).forEach(processFile);
  };
  const allCommRecords = useMemo(() => commFiles.flatMap(f=>f.records.map(r=>({...r, _srcFile: f.name}))), [commFiles]);
  const dueMonthToYYYYMM = (dm) => {
    if (!dm) return null;
    try {
      const d = new Date(dm + " 1");
      if (isNaN(d.getTime())) return null;
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    } catch(e) { return null; }
  };
  const runFuzzyMatch = () => {
    if (!allCommRecords.length) return;
    setIsRunning(true);
    setParseError("");
    setTimeout(() => {
     try {
      const activeClients = clients.filter(c => c.status !== "inactive");
      const inactiveClients = clients.filter(c => c.status === "inactive");
      const activeVariants = activeClients.map(c => ({ client: c, names: buildClientVariants(c) }));
      const inactiveVariants = inactiveClients.map(c => ({ client: c, names: buildClientVariants(c) }));
      const clientById = new Map(clients.map(c => [c.id, c]));
      const matched = new Set();
      const resultsArr = allCommRecords.map((rec, idx) => {
        // Remembered assignment first: if this exact name was assigned to a
        // client before, auto-link it and don't ask again (Lissette #2).
        const aliasId = nameAliasMap.get(_normName(rec.name));
        const aliasClient = aliasId ? clientById.get(aliasId) : null;
        let bestScore = 0, bestClient = null, isInactive = false, remembered = false;
        if (aliasClient) {
          bestScore = 1; bestClient = aliasClient; isInactive = aliasClient.status === "inactive"; remembered = true;
        } else {
          for (const cv of activeVariants) {
            const score = bestMatchScoreForClient(rec.name, cv.names);
            if (score > bestScore) { bestScore = score; bestClient = cv.client; }
          }
          if (bestScore < 0.65) {
            for (const cv of inactiveVariants) {
              const score = bestMatchScoreForClient(rec.name, cv.names);
              if (score > bestScore) { bestScore = score; bestClient = cv.client; isInactive = true; }
            }
          }
        }
        const matchedClient = (remembered || bestScore >= 0.65) ? bestClient : null;
        if (matchedClient) matched.add(idx);
        return {
          rawName: rec.name, amount: rec.commission,
          carrier: rec.issuer || carrierName || "Unknown",
          memberId: rec.policy || "", dueMonth: rec.dueMonth || "",
          saleType: rec.saleType || "", state: rec.state || "",
          // Line detected from the file's product/coverage column (health|dental
          // …), so a mixed statement imports each row with the right type.
          productType: productToPolicyType(rec.product || rec.saleType),
          srcFile: rec._srcFile || "",
          matched: matchedClient ? {...matchedClient, _matchScore: Math.round(bestScore*100)} : null,
          confirmed: !!matchedClient, isInactive, remembered,
          matchScore: Math.round(bestScore * 100),
        };
      }).filter(r => r.rawName);
      setMatchResults(resultsArr);
     } catch (err) {
      // Without this, an exception inside the setTimeout left isRunning
      // stuck true forever — the modal hung on "Processing..." with no
      // way to recover. Surface the error and always reset the flag.
      setParseError(`⚠️ ${lang==="es"?"Error al verificar los registros":"Error verifying records"}: ${err && err.message ? err.message : err}`);
     } finally {
      setIsRunning(false);
     }
    }, 100);
  };
  // OJO CON EL ARGUMENTO: colgado de un onClick, React le pasa el EVENTO del
  // clic como primer argumento, que es un objeto y por tanto verdadero — o sea
  // que "confirmar el duplicado" se activaria solo en cada subida y el aviso no
  // saltaria nunca. Por eso se exige `=== true` aqui dentro y no se confia en
  // que todos los sitios que lo llamen se acuerden.
  const confirmImport = async (confirmar) => {
    const confirmarDuplicado = confirmar === true;
    // Si la ventana de verificacion se cerro (o se pulso "Volver") con el aviso
    // de "archivo ya subido" abierto, la lista ya no existe: no hay nada que
    // subir. Antes esto tronaba (null.filter, DESKTE-SPA-56) y el boton se
    // quedaba en "Subiendo…" para siempre.
    if (!Array.isArray(matchResults)) {
      setDupAviso(null);
      toast.error(lang === "es" ? "La verificación se cerró. Vuelve a subir el archivo." : "The verification was closed. Please upload the file again.");
      return;
    }
    const batchTs = new Date().toISOString();
    const confirmedRecs = matchResults.filter(r=>r.confirmed).map(r => {
      const realMonth = dueMonthToYYYYMM(r.dueMonth) || month;
      return {
        id: uid(), month: realMonth, carrier: r.carrier || carrierName || "Unknown", policyType: r.productType || uploadPolicyType || "aca",
        clientNameRaw: r.rawName, clientId: r.matched?.id || null,
        memberIdRaw: r.memberId, amount: r.amount,
        matchScore: r.matchScore || 0, saleType: r.saleType || "", state: r.state || "",
        status: r.isInactive ? "inactive-paid" : "paid", source: "upload", dueMonth: r.dueMonth || "",
        importedAt: new Date().toISOString().split("T")[0],
        srcFile: r.srcFile || "", batchTs
      };
    });
    // Every row the user did NOT confirm is imported as "Not in CRM" —
    // INCLUDING auto-matched rows that were later unchecked. The old
    // `&& !r.matched` filter dropped matched-but-unconfirmed rows from
    // BOTH lists, so their money vanished silently and the import never
    // reconciled with the carrier file's grand total (lost ~$131 / 5 rows
    // on Lissette's April ACA file). For financial data nothing may be
    // dropped: confirmed → attributed to client; everything else → saved
    // unmatched so the totals always tie out to the statement.
    const unmatchedRecs = matchResults.filter(r=>!r.confirmed).map(r => {
      const realMonth = dueMonthToYYYYMM(r.dueMonth) || month;
      return {
        id: uid(), month: realMonth, carrier: r.carrier || carrierName || "Unknown", policyType: r.productType || uploadPolicyType || "aca",
        clientNameRaw: r.rawName, clientId: null,
        memberIdRaw: r.memberId, amount: r.amount,
        matchScore: 0, saleType: r.saleType || "", state: r.state || "",
        status: "unmatched", source: "upload", dueMonth: r.dueMonth || "",
        importedAt: new Date().toISOString().split("T")[0],
        srcFile: r.srcFile || "", batchTs
      };
    });
    const allNewRecs = [...confirmedRecs, ...unmatchedRecs];
    const dedup = (prev, recs) => prev.filter(c => !recs.find(n => n.clientNameRaw === c.clientNameRaw && n.carrier === c.carrier && n.month === c.month));
    if (isApiModeCom) {
      let serverRecs;
      try {
        serverRecs = await importCommissionBatch(allNewRecs, { confirmarDuplicado });
      } catch (err) {
        // ARCHIVO YA SUBIDO: no es un fallo, es una pregunta. Se enseña con los
        // numeros de la subida anterior y se deja decidir — el mismo nombre
        // puede ser el archivo del mes siguiente (Maricarmen, 2026-09-10).
        if (err.codigo === "SUBIDA_REPETIDA") {
          setDupAviso({ duplicados: err.duplicados || [], subiendo: false });
          return;
        }
        setDupAviso(null); // no dejar el aviso trabado en "Subiendo…"
        toast.error((lang === "es" ? "No se pudieron guardar las comisiones: " : "Failed to save commissions: ") + err.message);
        return;
      }
      // Use the server rows (real UUIDs) so refresh, delete and batch-delete
      // all work. Without this the upload only lived in localStorage and
      // vanished on the next page load.
      onCommissionsChange(prev => [...dedup(prev, serverRecs), ...serverRecs]);
    } else {
      onCommissionsChange(prev => [...dedup(prev, allNewRecs), ...allNewRecs]);
    }
    setUploadModal(false); setMatchResults(null); setCommFiles([]); setCarrierName(""); setParseError(""); setUploadPolicyType("aca"); setDupAviso(null);
  };
  const isApiModeCom = typeof window !== "undefined" && !!window.__deskteAuth;
  const addManual = async () => {
    if (!manualForm.clientId || !manualForm.amount) return;
    const c = clients.find(x=>x.id===manualForm.clientId);
    const localRec = {
      id:uid(), month, carrier: manualForm.carrier || "Manual",
      clientNameRaw: c ? `${c.firstName} ${c.lastName}` : "", clientId: manualForm.clientId,
      memberIdRaw: manualForm.memberId, amount: parseFloat(manualForm.amount),
      status:"pending", saleType:"new", source:"manual",
      importedAt: new Date().toISOString().split("T")[0]
    };
    if (isApiModeCom) {
      try {
        const created = await apiCreateCommission(localRec);
        onCommissionsChange(prev => [...prev, { ...localRec, ...created, source:"manual" }]);
      } catch (err) {
        toast.error((lang === "es" ? "No se pudo guardar la comisión: " : "Failed to save commission: ") + err.message);
        return;
      }
    } else {
      onCommissionsChange(prev => [...prev, { ...localRec, status:"manual" }]);
    }
    setManualModal(false); setManualForm({clientId:"",carrier:"",amount:"",memberId:"",month});
  };
  const isApiModeC = typeof window !== "undefined" && !!window.__deskteAuth;
  // Reassign / clear the client on an ALREADY-IMPORTED commission and persist
  // it (PATCH endpoint) so the fix survives a refresh — used by the "Not in
  // CRM" picker and the Paid-row reassign. clientId null = unassign back to
  // "Not in CRM". Falls back to a local-only update outside API mode.
  const reassignCommission = async (rec, clientId) => {
    if (isApiModeC) {
      try {
        const updated = await apiUpdateCommission(rec.id, { clientId: clientId || null });
        onCommissionsChange(prev => prev.map(c => c.id === rec.id ? { ...c, ...updated } : c));
      } catch (err) {
        toast.error((lang === "es" ? "No se pudo reasignar: " : "Failed to reassign: ") + (err?.message || err));
        return;
      }
    } else {
      onCommissionsChange(prev => prev.map(c => c.id === rec.id
        ? { ...c, clientId: clientId || null, status: clientId ? "paid" : "unmatched" }
        : c));
    }
    const nm = clientId ? clients.find(x => x.id === clientId) : null;
    toast.success(clientId
      ? (lang === "es" ? `Asignado a ${nm ? `${nm.firstName} ${nm.lastName}` : "cliente"}` : `Assigned to ${nm ? `${nm.firstName} ${nm.lastName}` : "client"}`)
      : (lang === "es" ? "Asignación quitada" : "Assignment cleared"));
  };
  // "+Cliente" opens a quick Add-Client window RIGHT HERE in Comisiones —
  // pre-filled with the name from the carrier file. Lissette 2026-06-23:
  // the old flow jumped to the Clients tab (losing her place) and the name
  // didn't come through; the full form also needed two Saves. Now the name
  // is pre-filled, a single Save creates the client (API + Clients list)
  // and the window closes WITHOUT leaving Comisiones. The live name-resolver
  // (#210) then links the commission and it moves to PAID.
  // Carrier files are usually "LAST FIRST" → last token = first name.
  const titleCase = (s) => s.replace(/\S+/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
  const addClientFromRec = (rec) => {
    const raw = (rec.clientNameRaw || rec.name || "").trim().replace(/\s+/g, " ");
    if (!raw) { toast.error(lang==="es"?"Sin nombre en el registro":"Record has no name"); return; }
    const parts = raw.split(" ");
    const firstName = titleCase(parts.length > 1 ? parts[parts.length - 1] : parts[0]);
    const lastName  = titleCase(parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0]);
    setAddClientForm({ firstName, lastName, phone: "", email: "", toProspect: false });
  };
  // Create the client from the quick window without leaving Comisiones.
  const saveNewClient = async () => {
    const f = addClientForm;
    if (!f) return;
    const firstName = (f.firstName || "").trim();
    const lastName = (f.lastName || "").trim();
    if (!firstName && !lastName) { toast.error(lang==="es"?"Escribe al menos el nombre":"Enter at least a name"); return; }
    // Lissette 2026-07-11: al agregar el cliente aquí se pregunta si va al
    // módulo de Prospecto y Referido. Sí → status "prospect" (aparece allá);
    // No → "active" (solo se crea la ficha).
    const payload = {
      firstName, lastName,
      phone: (f.phone || "").trim(),
      email: (f.email || "").trim(),
      status: f.toProspect ? "prospect" : "active",
      source: "commission",
      preferredLang: lang === "es" ? "spanish" : "english",
    };
    setSavingClient(true);
    try {
      if (isApiModeC) {
        const created = await apiCreateClient(payload);
        const merged = { ...payload, ...created };
        // SSE client.created can arrive before this resolves — merge instead
        // of pushing a duplicate (same pattern as the main Clients add flow).
        onClientsChange(prev => (prev || []).some(c => c.id === merged.id)
          ? prev.map(c => (c.id === merged.id ? { ...c, ...merged } : c))
          : [ merged, ...(prev || []) ]);
      } else {
        const local = { ...payload, id: uid(), createdAt: new Date().toISOString().split("T")[0] };
        onClientsChange(prev => [ local, ...(prev || []) ]);
      }
      const fullName = `${firstName} ${lastName}`.trim();
      toast.success(lang==="es"
        ? `Cliente "${fullName}" creado — se enlazará a su comisión`
        : `Client "${fullName}" created — it will link to its commission`);
      setAddClientForm(null);
    } catch (err) {
      toast.error((lang==="es"?"No se pudo guardar el cliente: ":"Failed to save client: ") + (err?.message||err));
    } finally {
      setSavingClient(false);
    }
  };
  const deleteRecord = async id => {
    if (isApiModeC) {
      try {
        await apiDeleteCommission(id);
      } catch (err) {
        // 404 = record was SEED-only (never in backend) → still remove from UI
        // Anything else = real failure → alert and abort
        if (!String(err.message || '').includes('404')) {
          toast.error((lang === "es" ? "No se pudo eliminar: " : "Failed to delete: ") + err.message);
          return;
        }
      }
    }
    onCommissionsChange(prev => prev.filter(c => c.id !== id));
  };
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {key, label, count}
  // Aviso de "este archivo ya se subio": { duplicados, subiendo }. El servidor
  // contesta 409 con los numeros de la subida anterior y aqui se decide.
  const [dupAviso, setDupAviso] = useState(null);
  // Las comisiones que no vienen de ninguna subida: verlas y poder borrarlas.
  // Ensenar el descuadre sin dejar tocarlo era medio arreglo (Lissette,
  // 2026-09-11: "eso es un dolor de cabeza").
  const [verFuera, setVerFuera] = useState(false);
  const [confirmFuera, setConfirmFuera] = useState(false);
  const [borrandoFuera, setBorrandoFuera] = useState(false);
  // Borrar una subida: el boton se bloquea mientras borra. Sin esto, 2-3 clics
  // mandaban los mismos DELETE a la vez y el servidor contestaba 500 (ronda de
  // ~100 errores el 2026-10-27 23:09 UTC).
  const [borrandoSubida, setBorrandoSubida] = useState(false);
  // Quick Add-Client window (from the "+Cliente" on an unmatched commission).
  // Holds { firstName, lastName, phone, email } or null. Stays inside Comisiones.
  const [addClientForm, setAddClientForm] = useState(null);
  const [savingClient, setSavingClient] = useState(false);
  const uploadBatches = useMemo(() => {
    const batches = {};
    // Agrupar y cuadrar usan LA MISMA funcion: si una dice "esto es una subida"
    // y la otra no, la misma comision sale en las dos mitades y el total se
    // duplica (se vio en el navegador, no en las pruebas).
    (commissions||[]).filter(vieneDeUnaSubida).forEach(r => {
      // Group by srcFile (new uploads track per-file). Old records without srcFile group by importedAt.
      const key = r.srcFile ? `${r.importedAt||""}_${r.srcFile}` : `${r.importedAt||"unknown"}_all`;
      if (!batches[key]) batches[key] = {
        date: r.importedAt||"unknown", ts: r.batchTs||"",
        fileName: r.srcFile || "",
        fileList: r.batchFiles ? r.batchFiles.split(", ").filter(Boolean) : [],
        carriers: new Set(), policyType: r.policyType||"aca",
        count: 0, total: 0, months: new Set(), ids: []
      };
      batches[key].count++;
      batches[key].total += parseFloat(r.amount||0);
      if(r.month) batches[key].months.add(r.month);
      if(r.carrier) batches[key].carriers.add(r.carrier);
      batches[key].ids.push(r.id);
    });
    return Object.entries(batches).map(([key,b])=>({
      key, ...b, months:[...b.months].sort(), carriers:[...b.carriers].sort()
    })).sort((a,b)=>(b.ts||b.date).localeCompare(a.ts||a.date));
  }, [commissions]);
  // EL CUADRE: subidas + lo que no viene de ninguna = TODO el dinero. Sin esto
  // el historial enseña una parte y quien lo mira cree que ya no queda nada
  // (Maricarmen Nieto, 2026-09-10: borraba todo y el monto seguia sin cuadrar).
  const cuadre = useMemo(()=>cuadreDeComisiones(commissions, uploadBatches), [commissions, uploadBatches]);
  const fueraDeSubidas = useMemo(()=>filasFueraDeSubidas(commissions), [commissions]);
  // BORRAR COMISIONES EN EL SERVIDOR, en tandas. Lo usan el borrado de una
  // subida y el de lo que no viene de ninguna, para que las dos se comporten
  // igual. En tandas de 25 y no todas a la vez: un lote de 500 abria 500
  // peticiones de golpe. Devuelve los fallos de verdad (un 404 es una fila que
  // ya no estaba, y eso no es un fallo).
  const borrarComisionesEnElServidor = async (ids) => {
    const failures = [];
    // Sin repetidos: el mismo id dos veces en la misma tanda choca consigo mismo.
    const unicos = [...new Set(ids)];
    for (let i = 0; i < unicos.length; i += 25) {
      await Promise.all(unicos.slice(i, i + 25).map(async id => {
        try { await apiDeleteCommission(id); }
        catch (err) {
          if (!String(err.message || '').includes('404')) failures.push({ id, msg: err.message });
        }
      }));
    }
    return failures;
  };
  const deleteBatch = async (key) => {
    if (borrandoSubida) return;
    const batch = uploadBatches.find(b=>b.key===key);
    if(!batch) return;
    setBorrandoSubida(true);
    try {
      if (isApiModeC) {
        const failures = await borrarComisionesEnElServidor(batch.ids);
        if (failures.length) {
          toast.error((lang === "es" ? "Algunas comisiones no se pudieron eliminar: " : "Some commissions could not be deleted: ") + failures.map(f => f.msg).join(', '));
          return;
        }
      }
      onCommissionsChange(prev=>prev.filter(c=>!batch.ids.includes(c.id)));
      setDeleteConfirm(null);
    } finally { setBorrandoSubida(false); }
  };
  // Lo que no viene de ninguna subida, borrado de verdad: se va del servidor y
  // deja de contar en el total, que es justo lo que se pidio.
  const borrarLoDeFuera = async () => {
    const ids = fueraDeSubidas.map(c=>c.id);
    if (!ids.length) { setConfirmFuera(false); return; }
    setBorrandoFuera(true);
    try {
      if (isApiModeC) {
        const failures = await borrarComisionesEnElServidor(ids);
        if (failures.length) {
          toast.error((lang === "es" ? "Algunas comisiones no se pudieron eliminar: " : "Some commissions could not be deleted: ") + failures.map(f => f.msg).join(', '));
          return;
        }
      }
      const fuera = new Set(ids);
      onCommissionsChange(prev=>prev.filter(c=>!fuera.has(c.id)));
      toast.success(lang==="es" ? `${ids.length} comisión(es) eliminada(s)` : `${ids.length} commission(s) deleted`);
      setConfirmFuera(false); setVerFuera(false);
    } finally { setBorrandoFuera(false); }
  };
  // Una sola comision, desde la lista de lo que quedo fuera.
  const borrarUnaDeFuera = async (id) => {
    if (isApiModeC) {
      const failures = await borrarComisionesEnElServidor([id]);
      if (failures.length) { toast.error(failures[0].msg); return; }
    }
    onCommissionsChange(prev=>prev.filter(c=>c.id!==id));
  };
  useEffect(() => {
    // F-N4: SRI-pinned CDN loads via cdnVendor registry.
    (async () => {
      const { loadCdnScript, XLSX_FULL_0_18_5, PDFJS_3_11_174, PDFJS_WORKER_3_11_174 } = await import("../cdnVendor.js");
      if (typeof window.XLSX === "undefined") {
        loadCdnScript(XLSX_FULL_0_18_5).catch(() => { /* surfaces at use site */ });
      }
      if (typeof window.pdfjsLib === "undefined") {
        try {
          await loadCdnScript(PDFJS_3_11_174);
          await loadCdnScript(PDFJS_WORKER_3_11_174);
          if (window.pdfjsLib) {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_3_11_174.url;
          }
        } catch { /* surfaces at use site */ }
      }
    })();
  }, []);
  return (
    <div>
      <div style={{display:"flex",flexWrap:"wrap",alignItems:"flex-start",justifyContent:"space-between",gap:"1rem",marginBottom:"1.25rem"}}>
        <div>
          <div className="dkt-sec-title">💰 {lang==="es"?"Comisiones":"Commissions"}</div>
          <div className="dkt-sec-sub">{lang==="es"?"Sube archivos y verifica quién pagó cada mes":"Upload carrier files and verify who paid each month"}</div>
        </div>
        {/* Without the permission the buttons stay VISIBLE but disabled, with
            the reason spelled out. The first version of this gate hid them
            outright: a seat opened Commissions, found an empty gap where the
            upload button used to be, and reported the CRM as broken instead of
            asking their agent for the permission (Yilda 2026-08-11). A missing
            permission has to read as "you need access", never as "it vanished".
            Still not clickable — the point of the gate was to stop a seat from
            parsing a 154-row file only to hit a 403 on the final save. */}
        <div style={{display:"flex",flexWrap:"wrap",gap:".4rem",alignItems:"center"}}>
          {canEditComm ? (<>
            <button onClick={()=>setManualModal(true)} className="dkt-btn-g"><Plus size={12}/>{lang==="es"?"Manual":"Add Manual"}</button>
            <button onClick={()=>setUploadModal(true)} className="dkt-btn-p"><Upload size={13}/>{lang==="es"?"Subir Archivo":"Upload File"}</button>
          </>) : (<>
            <span style={{fontSize:".64rem",color:"var(--c-text3,#45688a)",display:"inline-flex",alignItems:"center",gap:4,maxWidth:290,lineHeight:1.3}}>
              🔒 {lang==="es"
                ? "Pídele a tu agente el permiso “Subir / editar comisiones” (Mi Equipo)"
                : "Ask your agent for the “Import / edit commissions” permission (My Team)"}
            </span>
            {[[Plus,12,lang==="es"?"Manual":"Add Manual","dkt-btn-g"],[Upload,13,lang==="es"?"Subir Archivo":"Upload File","dkt-btn-p"]].map(([Icon,sz,label,cls])=>(
              <button key={label} type="button" disabled
                title={lang==="es"
                  ? "Necesitas el permiso “Subir / editar comisiones”. Tu agente lo activa en Mi Equipo → tu nombre → Pólizas y Comisiones."
                  : "You need the “Import / edit commissions” permission. Your agent turns it on in My Team → your name → Policies & Commissions."}
                className={cls} style={{opacity:.4,cursor:"not-allowed"}}>
                <Icon size={sz}/>{label}
              </button>
            ))}
          </>)}
        </div>
      </div>
      <div style={{display:"flex",gap:".4rem",marginBottom:"1rem",flexWrap:"wrap",borderBottom:"1px solid var(--c-border,#1a3550)",paddingBottom:".6rem"}}>
        <button onClick={()=>setTab("tracker")} className={`dkt-tab${tab==="tracker"?" on":""}`}>📋 {lang==="es"?"Tracker":"Tracker"}</button>
        <button onClick={()=>setTab("timeline")} className={`dkt-tab${tab==="timeline"?" on":""}`}>📅 Timeline</button>
        <button onClick={()=>setTab("history")} className={`dkt-tab${tab==="history"?" on":""}`}>🗂️ {lang==="es"?"Historial":"History"}</button>
      </div>
      {tab==="tracker" && <>
        {(commissions||[]).some(r=>!r.policyType) && (
          <div style={{
            background:"rgba(255,183,0,.08)",border:"1px solid rgba(255,183,0,.3)",
            borderRadius:9,padding:".5rem .875rem",marginBottom:".75rem",
            display:"flex",alignItems:"center",justifyContent:"space-between",gap:".75rem",
          }}>
            <span style={{fontSize:".73rem",color:"var(--c-amber,#ffb700)"}}>
              ⚠️ {lang==="es"?"Algunos registros no tienen tipo de póliza (se muestran como Salud/ACA). Usa Reset para datos limpios.":"Some records are missing policy type (shown as Health/ACA). Use Reset for clean data."}
            </span>
          </div>
        )}
        {availableYears.length > 1 && <div style={{display:"flex",gap:".3rem",marginBottom:".65rem",flexWrap:"wrap"}}>
          {(() => {
            const allYearsTotal = normalizedComms.filter(c=>c.amount>0).reduce((s,r)=>s+r.amount,0);
            const allNet = normalizedComms.reduce((s,r)=>s+(Number(r.amount)||0),0);
            const on = yearFilter === "all";
            return <button key="all" onClick={()=>{setYearFilter("all");setMonth("all");setCarrierSel([]);setStateSel([]);}} style={{padding:".35rem .75rem",borderRadius:20,cursor:"pointer",fontSize:".75rem",fontWeight:on?800:600,background:on?"var(--c-green,#00ff88)":"var(--c-surface2,#112538)",border:`1px solid ${on?"var(--c-green,#00ff88)":"var(--c-border,#1a3550)"}`,color:on?"#070f1a":"var(--c-text2,#7aa3c0)",fontFamily:"var(--font-body)",transition:"all .15s",display:"flex",alignItems:"center",gap:".35rem"}}>
              <span>{lang==="es"?"Todos":"All"}</span>
              <span style={{fontSize:".6rem",opacity:.8,fontFamily:"var(--font-mono)"}}>{fmtMoney(allYearsTotal)}{allNet!==allYearsTotal?` · ${lang==="es"?"Neto":"Net"} ${fmtMoney(allNet)}`:""}</span>
            </button>;
          })()}
          {availableYears.map(yr=>{
            const on=yearFilter===yr;
            const yrTotal=normalizedComms.filter(c=>c.month?.startsWith(yr)&&c.amount>0).reduce((s,r)=>s+r.amount,0);
            const yrNet=normalizedComms.filter(c=>c.month?.startsWith(yr)).reduce((s,r)=>s+(Number(r.amount)||0),0);
            return <button key={yr} onClick={()=>{setYearFilter(yr);setMonth("all");setCarrierSel([]);setStateSel([]);}} style={{padding:".35rem .75rem",borderRadius:20,cursor:"pointer",fontSize:".75rem",fontWeight:on?800:600,background:on?"var(--c-cyan,#00d4ff)":"var(--c-surface2,#112538)",border:`1px solid ${on?"var(--c-cyan,#00d4ff)":"var(--c-border,#1a3550)"}`,color:on?"#070f1a":"var(--c-text2,#7aa3c0)",fontFamily:"var(--font-body)",transition:"all .15s",display:"flex",alignItems:"center",gap:".35rem"}}>
              <span>{yr}</span>
              <span style={{fontSize:".6rem",opacity:.8,fontFamily:"var(--font-mono)"}}>{fmtMoney(yrTotal)}{yrNet!==yrTotal?` · ${lang==="es"?"Neto":"Net"} ${fmtMoney(yrNet)}`:""}</span>
            </button>;
          })}
        </div>}
        <div style={{display:"flex",alignItems:"center",gap:".75rem",marginBottom:"1rem",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:".5rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:9,padding:".45rem .875rem"}}>
            <Calendar size={13} style={{color:"var(--c-text3,#45688a)"}}/>
            <select value={month} onChange={e=>{setMonth(e.target.value);setCarrierSel([]);setStateSel([]);}} style={{background:"transparent",border:"none",outline:"none",fontWeight:700,fontSize:".82rem",color:"var(--c-white,#eef6ff)",cursor:"pointer"}}>
              <option value="all">{yearFilter==="all"?(lang==="es"?"Todos los Meses":"All Months"):(lang==="es"?`Todo ${yearFilter}`:`All ${yearFilter}`)}</option>
              {months.filter(m=>yearFilter==="all"||m.startsWith(yearFilter)).map(m=><option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
          </div>
          {monthCarriers.length > 0 && <MultiSelectFilter options={monthCarriers} selected={carrierSel} onChange={setCarrierSel} allLabel={lang==="es"?"Todas las Compañías":"All Carriers"} icon={<FileText size={13} style={{color:"var(--c-text3,#45688a)"}}/>} lang={lang}/>}
          {monthStates.length > 0 && <MultiSelectFilter options={monthStates} selected={stateSel} onChange={setStateSel} allLabel={lang==="es"?"Todos los Estados":"All States"} icon={<span style={{fontSize:".8rem"}}>📍</span>} lang={lang} width={150}/>}
          {/* Client search + active/inactive filter (Lissette 2026-07-13). */}
          <div style={{position:"relative",minWidth:180}}>
            <Search size={12} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--c-text3,#45688a)",pointerEvents:"none"}}/>
            <input value={trackerSearch} onChange={e=>setTrackerSearch(e.target.value)}
              placeholder={lang==="es"?"Buscar cliente...":"Search client..."}
              className="dkt-search" style={{paddingLeft:30}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:".5rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:9,padding:".45rem .75rem"}}>
            <select value={trackerStatus} onChange={e=>setTrackerStatus(e.target.value)} style={{background:"transparent",border:"none",outline:"none",fontWeight:700,fontSize:".8rem",color:"var(--c-white,#eef6ff)",cursor:"pointer"}}>
              <option value="all">{lang==="es"?"Activos e inactivos":"Active & inactive"}</option>
              <option value="active">{lang==="es"?"Solo activos":"Active only"}</option>
              <option value="inactive">{lang==="es"?"Solo inactivos":"Inactive only"}</option>
            </select>
          </div>
          <div style={{fontSize:".72rem",fontFamily:"var(--font-mono)",display:"flex",gap:".75rem",alignItems:"center"}}>
            <span style={{color:"var(--c-text3,#45688a)"}}>{monthComms.length} records</span>
            <span style={{color:"var(--c-green,#00ff88)",fontWeight:700}}>{fmtMoney(totalReceived)}</span>
            {chargebackTotal<0&&<span style={{color:"var(--c-red,#ff3d6b)",fontWeight:700}}>{fmtMoney(chargebackTotal)} chargebacks</span>}
          </div>
          <div style={{marginLeft:"auto",display:"flex",gap:".4rem"}}>
            <button type="button" onClick={printPaymentReport} title={lang==="es"?"Imprimir o guardar como PDF los pagos filtrados":"Print or save as PDF the filtered payments"}
              style={{display:"flex",alignItems:"center",gap:".35rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:9,padding:".45rem .75rem",cursor:"pointer",color:"var(--c-white,#eef6ff)",fontWeight:700,fontSize:".76rem",fontFamily:"inherit"}}>
              🖨️ {lang==="es"?"Imprimir / PDF":"Print / PDF"}
            </button>
            <button type="button" onClick={exportPaymentCsv} title={lang==="es"?"Descargar los pagos filtrados en Excel/CSV":"Download the filtered payments as Excel/CSV"}
              style={{display:"flex",alignItems:"center",gap:".35rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:9,padding:".45rem .75rem",cursor:"pointer",color:"var(--c-white,#eef6ff)",fontWeight:700,fontSize:".76rem",fontFamily:"inherit"}}>
              📊 Excel/CSV
            </button>
          </div>
        </div>
        <div style={{display:"flex",gap:".35rem",flexWrap:"wrap",marginBottom:".875rem"}}>
          {COMM_POLICY_TYPES.map(pt=>{
            const allM=(commissions||[]).map(r=>({...r,policyType:r.policyType||"aca"})).filter(r=>yearMatch(r)&&r.amount>0);
            const rows=pt.id==="all"?allM:allM.filter(r=>r.policyType===pt.id);
            if(pt.id!=="all"&&rows.length===0) return null;
            const tot=rows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
            const on=policyTypeFilter===pt.id;
            return <button key={pt.id} type="button" onClick={()=>setPolicyTypeFilter(pt.id)} style={{
              padding:".35rem .75rem",borderRadius:20,cursor:"pointer",fontSize:".71rem",
              fontWeight:on?800:600,background:on?pt.color:"var(--c-surface2,#112538)",
              border:`1px solid ${on?pt.color:"var(--c-border,#1a3550)"}`,
              color:on?"#070f1a":"var(--c-text2,#7aa3c0)",
              fontFamily:"var(--font-body)",transition:"all .15s",
              display:"flex",alignItems:"center",gap:".3rem",
            }}>
              <span>{pt.icon}</span>
              <span>{lang==="es"?pt.label:pt.labelEn}</span>
              <span style={{fontSize:".58rem",opacity:.8,fontFamily:"var(--font-mono)"}}>{rows.length} · {fmtMoney(tot)}</span>
            </button>;
          })}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:".6rem",marginBottom:"1.1rem"}}>
          {[
            {id:"all",icon:"💵",label:lang==="es"?"Total":"Total",val:fmtMoney(totalReceived),sub:chargebackTotal<0?`${lang==="es"?"Neto":"Net"}: ${fmtMoney(totalReceived+chargebackTotal)}`:null,accent:"#00d4ff",bg:"rgba(0,212,255,.08)"},
            {id:"paid",icon:"✅",label:lang==="es"?"Pagados":"PAID",val:`${paidRows.length} clientes`,accent:"#06d6a0",bg:"rgba(6,214,160,.08)"},
            {id:"notpaid",icon:"❌",label:lang==="es"?"Sin Pago":"NOT PAID",val:`${missingRows.length} clientes`,accent:"#ff3d6b",bg:"rgba(255,61,107,.08)"},
            {id:"inactive",icon:"🗂️",label:"Inactive CRM",val:`${inactivePaid.length} (${fmtMoney(inactivePaidTotal)})`,accent:"#a78bfa",bg:"rgba(167,139,250,.1)"},
            {id:"notincrm",icon:"⚠️",label:"Not in CRM",val:`${notInCRM.length} (${fmtMoney(notInCRMTotal)})`,accent:"#ffb700",bg:"rgba(255,183,0,.08)"},
            {id:"chargebacks",icon:"⛔",label:"Chargebacks",val:`${chargebacks.length} (${fmtMoney(chargebackTotal)})`,accent:"#ef4444",bg:"rgba(239,68,68,.08)"},
          ].map((s,i)=>(
            <div key={i} onClick={()=>{if(s.id==="notincrm"){setTab("notincrm");setKpiFilter("all");}else if(s.id==="chargebacks"){setTab("chargebacks");setKpiFilter("all");}else{setKpiFilter(kpiFilter===s.id?"all":s.id);}}} style={{background:(kpiFilter===s.id||tab===s.id)?s.accent+"22":s.bg,border:`1px solid ${(kpiFilter===s.id||tab===s.id)?s.accent:s.accent+"33"}`,borderRadius:10,padding:".65rem .75rem",textAlign:"center",cursor:"pointer",transition:"all .18s",transform:(kpiFilter===s.id||tab===s.id)?"scale(1.03)":"scale(1)"}}>
              <div style={{fontSize:"1.1rem",marginBottom:2}}>{s.icon}</div>
              <div style={{fontFamily:"var(--font-display)",fontSize:".95rem",fontWeight:700,color:s.accent,lineHeight:1.1}}>{s.val}</div>
              {s.sub&&<div style={{fontSize:".58rem",color:"#ef4444",marginTop:2,fontFamily:"var(--font-mono)",fontWeight:700}}>{s.sub}</div>}
              <div style={{fontSize:".6rem",color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".07em",marginTop:3,fontFamily:"var(--font-mono)"}}>{s.label}</div>
            </div>
          ))}
        </div>
        {(paidRows.length > 0 || missingRows.length > 0) && <>
          <div style={{marginBottom:"1rem"}}>
            <div style={{fontSize:".68rem",color:"var(--c-text3,#45688a)",marginBottom:".4rem",fontFamily:"var(--font-mono)",textTransform:"uppercase",letterSpacing:".06em"}}>{lang==="es"?"Cobertura de Pago":"Payment Coverage"}</div>
            <div style={{display:"flex",height:24,borderRadius:8,overflow:"hidden",border:"1px solid var(--c-border,#1a3550)"}}>
              {paidRows.length>0&&<div style={{width:`${(paidRows.length/(paidRows.length+missingRows.length))*100}%`,minWidth:30,background:"linear-gradient(90deg,var(--c-green,#00ff88),#06d6a0)",display:"flex",alignItems:"center",justifyContent:"center",color:"#070f1a",fontSize:".62rem",fontWeight:800,fontFamily:"var(--font-mono)"}}>{paidRows.length} paid</div>}
              {missingRows.length>0&&<div style={{flex:1,background:"rgba(255,61,107,.15)",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--c-red,#ff3d6b)",fontSize:".62rem",fontWeight:700,fontFamily:"var(--font-mono)"}}>{missingRows.length} not paid</div>}
            </div>
          </div>
          {(() => {
            const byCompNotPaid = {}; const byCompPaid = {};
            missingRows.forEach(({client}) => { const co = client.carrier || client.plan || "Other"; byCompNotPaid[co] = (byCompNotPaid[co]||0)+1; });
            paidRows.forEach(({client}) => { const co = client.carrier || client.plan || "Other"; byCompPaid[co] = (byCompPaid[co]||0)+1; });
            const notPaidEntries = Object.entries(byCompNotPaid).sort((a,b)=>b[1]-a[1]);
            const paidEntries = Object.entries(byCompPaid).sort((a,b)=>b[1]-a[1]);
            return (notPaidEntries.length > 0 || paidEntries.length > 0) ? (
              <div style={{marginBottom:"1rem"}}>
                {notPaidEntries.length > 0 && <>
                  <div style={{fontSize:".68rem",fontWeight:700,color:"var(--c-red,#ff3d6b)",marginBottom:".4rem",fontFamily:"var(--font-mono)"}}>❌ {lang==="es"?"Sin Pago por Compañía":"Not Paid by Company"}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:".35rem",marginBottom:".6rem"}}>
                    {notPaidEntries.map(([co,cnt])=>(
                      <span key={co} style={{background:"rgba(255,61,107,.1)",border:"1px solid rgba(255,61,107,.2)",borderRadius:8,padding:".3rem .6rem",fontSize:".68rem",display:"flex",alignItems:"center",gap:".4rem",color:"var(--c-text,#c8dff0)"}}>
                        <strong>{co}</strong><span style={{background:"var(--c-red,#ff3d6b)",color:"#fff",borderRadius:10,padding:"1px 7px",fontSize:".6rem",fontWeight:700}}>{cnt}</span>
                      </span>
                    ))}
                  </div>
                </>}
                {paidEntries.length > 0 && <>
                  <div style={{fontSize:".68rem",fontWeight:700,color:"var(--c-green,#00ff88)",marginBottom:".4rem",fontFamily:"var(--font-mono)"}}>✅ {lang==="es"?"Pagados por Compañía":"Paid by Company"}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:".35rem",marginBottom:".6rem"}}>
                    {paidEntries.map(([co,cnt])=>(
                      <span key={co} style={{background:"rgba(0,255,136,.06)",border:"1px solid rgba(0,255,136,.15)",borderRadius:8,padding:".3rem .6rem",fontSize:".68rem",display:"flex",alignItems:"center",gap:".4rem",color:"var(--c-text,#c8dff0)"}}>
                        <strong>{co}</strong><span style={{background:"var(--c-green,#00ff88)",color:"#070f1a",borderRadius:10,padding:"1px 7px",fontSize:".6rem",fontWeight:700}}>{cnt}</span>
                      </span>
                    ))}
                  </div>
                </>}
              </div>
            ) : null;
          })()}
        </>}
        {kpiFilter!=="all"&&<div style={{display:"flex",alignItems:"center",gap:".5rem",marginBottom:".6rem"}}>
          <span style={{fontSize:".72rem",color:"var(--c-text2,#7aa3c0)",fontFamily:"var(--font-mono)"}}>
            {lang==="es"?"Filtrado por:":"Filtered by:"} <strong style={{color:"var(--c-white,#eef6ff)"}}>{kpiFilter==="paid"?(lang==="es"?"Pagados":"Paid"):kpiFilter==="notpaid"?(lang==="es"?"Sin Pago":"Not Paid"):kpiFilter==="inactive"?"Inactive CRM":"Not in CRM"}</strong>
          </span>
          <button onClick={()=>setKpiFilter("all")} style={{background:"none",border:"1px solid var(--c-border,#1a3550)",borderRadius:6,color:"var(--c-text3,#45688a)",fontSize:".65rem",padding:".15rem .5rem",cursor:"pointer",fontFamily:"var(--font-mono)"}}>✕ {lang==="es"?"Limpiar":"Clear"}</button>
        </div>}
        <div className="dkt-card" style={{overflow:"hidden",marginBottom:".75rem"}}>
          {paidRows.length > 0 && (kpiFilter==="all"||kpiFilter==="paid") && <>
            <div style={{padding:".5rem 1rem",background:"var(--c-green-dim)",borderBottom:"1px solid rgba(0,255,136,.15)",display:"flex",alignItems:"center",gap:".5rem"}}>
              <CheckCircle size={12} style={{color:"var(--c-green,#00ff88)"}}/>
              <span style={{fontSize:".7rem",fontWeight:700,color:"var(--c-green,#00ff88)",textTransform:"uppercase",letterSpacing:".08em",fontFamily:"var(--font-mono)"}}>{lang==="es"?"PAGARON":"PAID"} - {paidRows.length}</span>
            </div>
            {paidRows.map(({client,record}) => (
              <div key={client.id} style={{display:"flex",alignItems:"center",gap:".75rem",padding:".65rem 1rem",borderBottom:"1px solid var(--c-border,#1a3550)"}}>
                <Avatar client={client} size={8}/>
                <div style={{flex:1,minWidth:0}}>
                  <ClientName client={client} color="var(--c-white,#eef6ff)" carrier={record.carrier}/>
                  <div style={{display:"flex",gap:".5rem",alignItems:"center",marginTop:2}}>
                    <span style={{fontSize:".68rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",color:"var(--c-text2,#7aa3c0)",padding:".1rem .45rem",borderRadius:6,fontFamily:"var(--font-mono)"}}>{record.carrier}</span>
                    {record.memberIdRaw&&<span style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontFamily:"var(--font-mono)"}}>#{record.memberIdRaw}</span>}
                    <span style={{fontSize:".62rem",padding:".1rem .4rem",borderRadius:5,background:record.source==="upload"?"rgba(0,212,255,.1)":"rgba(167,139,250,.1)",color:record.source==="upload"?"var(--c-cyan,#00d4ff)":"#a78bfa",fontWeight:700}}>{record.source==="upload"?"📂 Upload":"✏️ Manual"}</span>
                    {record.matchScore>0&&<span style={{fontSize:".58rem",padding:".1rem .4rem",borderRadius:5,background:"rgba(0,212,255,.08)",color:"var(--c-cyan,#00d4ff)",fontWeight:700,fontFamily:"var(--font-mono)"}}>{record.matchScore}% match</span>}
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:".95rem",color:"var(--c-green,#00ff88)"}}>{fmtMoney(record.amount)}</div>
                  <div style={{fontSize:".62rem",color:"var(--c-text3,#45688a)"}}>{record.clientNameRaw}</div>
                </div>
                {/* Reassign a wrong client without re-uploading; persists via PATCH (Lissette #3). */}
                {canEditComm&&record.id&&<ClientPicker clients={clients} lang={lang} onPick={cid=>{ if(cid&&cid!==client.id) reassignCommission(record, cid); }}/>}
                {canDeleteComm&&<button onClick={()=>deleteRecord(record.id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",padding:4,borderRadius:6,transition:"color .2s"}} onMouseEnter={e=>e.currentTarget.style.color="var(--c-red,#ff3d6b)"} onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Trash2 size={13}/></button>}
              </div>
            ))}
          </>}
          {inactivePaid.length > 0 && (kpiFilter==="all"||kpiFilter==="inactive") && <>
            <div style={{padding:".5rem 1rem",background:"rgba(167,139,250,.08)",borderBottom:"1px solid rgba(167,139,250,.15)",display:"flex",alignItems:"center",gap:".5rem"}}>
              <span style={{fontSize:11}}>😴</span>
              <span style={{fontSize:".7rem",fontWeight:700,color:"#a78bfa",textTransform:"uppercase",letterSpacing:".08em",fontFamily:"var(--font-mono)"}}>INACTIVE CRM - {inactivePaid.length}</span>
            </div>
            {inactivePaid.map(({client,record}) => (
              <div key={client.id} style={{display:"flex",alignItems:"center",gap:".75rem",padding:".65rem 1rem",borderBottom:"1px solid var(--c-border,#1a3550)",opacity:.75}}>
                <Avatar client={client} size={8}/>
                <div style={{flex:1}}>
                  <ClientName client={client} color="var(--c-text,#c8dff0)" carrier={record.carrier}/>
                  <div style={{fontSize:".68rem",color:"var(--c-text3,#45688a)"}}>Inactive in CRM . {record.carrier}</div>
                </div>
                <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:".9rem",color:"#a78bfa"}}>{fmtMoney(record.amount)}</div>
                {canDeleteComm&&<button onClick={()=>deleteRecord(record.id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",padding:4,borderRadius:6}} onMouseEnter={e=>e.currentTarget.style.color="var(--c-red,#ff3d6b)"} onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Trash2 size={13}/></button>}
              </div>
            ))}
          </>}
          {missingRows.length > 0 && (kpiFilter==="all"||kpiFilter==="notpaid") && <>
            <div style={{padding:".5rem 1rem",background:"var(--c-red-dim)",borderBottom:"1px solid rgba(255,61,107,.15)",display:"flex",alignItems:"center",gap:".5rem"}}>
              <AlertTriangle size={12} style={{color:"var(--c-red,#ff3d6b)"}}/>
              <span style={{fontSize:".7rem",fontWeight:700,color:"var(--c-red,#ff3d6b)",textTransform:"uppercase",letterSpacing:".08em",fontFamily:"var(--font-mono)"}}>{lang==="es"?"SIN REGISTRO":"MISSING"} - {missingRows.length}</span>
            </div>
            {missingRows.map(({client}) => (
              <div key={client.id} style={{display:"flex",alignItems:"center",gap:".75rem",padding:".65rem 1rem",borderBottom:"1px solid var(--c-border,#1a3550)",background:"rgba(255,61,107,.03)"}}>
                <Avatar client={client} size={8}/>
                <div style={{flex:1}}>
                  <ClientName client={client} color="var(--c-text,#c8dff0)" carrier={client.carrier}/>
                  <div style={{fontSize:".68rem",color:"var(--c-text3,#45688a)"}}>{client.carrier||"--"} . {client.plan||"--"}</div>
                </div>
                <span style={{fontSize:".68rem",background:"var(--c-red-dim)",color:"var(--c-red,#ff3d6b)",padding:".25rem .6rem",borderRadius:7,border:"1px solid rgba(255,61,107,.2)",fontWeight:700}}>⚠️ No recibida</span>
                <button onClick={()=>{ setManualForm(f=>({...f,clientId:client.id,carrier:client.carrier||""})); setManualModal(true); }} style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",padding:4,borderRadius:6}} onMouseEnter={e=>e.currentTarget.style.color="var(--c-cyan,#00d4ff)"} onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Plus size={14}/></button>
              </div>
            ))}
          </>}
          {visibleTrackerRows.length===0&&clients.length>0&&(trackerSearch.trim()||trackerStatus!=="all"||stateSel.length>0)&&(
            <div style={{textAlign:"center",padding:"2.5rem",color:"var(--c-text3,#45688a)",fontSize:".82rem"}}>
              {lang==="es"?"Ningún cliente coincide con la búsqueda o el filtro.":"No clients match the search or filter."}
            </div>
          )}
          {hiddenNoState>0&&(
            <div style={{textAlign:"center",padding:".6rem",color:"var(--c-text3,#45688a)",fontSize:".7rem"}}>
              {lang==="es"
                ? `${hiddenNoState} cliente${hiddenNoState===1?"":"s"} sin estado en la ficha queda${hiddenNoState===1?"":"n"} fuera de este filtro.`
                : `${hiddenNoState} client${hiddenNoState===1?"":"s"} with no state on file ${hiddenNoState===1?"is":"are"} excluded by this filter.`}
            </div>
          )}
          {monthComms.length===0&&clients.length===0&&(
            <div style={{textAlign:"center",padding:"3rem",color:"var(--c-text3,#45688a)",fontSize:".82rem"}}>No hay datos. Sube un archivo o agrega manualmente.</div>
          )}
        </div>
      </>}
      {tab==="timeline" && <>
        <div style={{marginBottom:"1rem"}}>
          <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:"1rem",color:"var(--c-white,#eef6ff)",marginBottom:".25rem"}}>📅 {lang==="es"?"Línea de Tiempo de Pagos":"Client Payment Timeline"}</div>
          <div style={{fontSize:".72rem",color:"var(--c-text3,#45688a)"}}>
            {lang==="es"?"Verifica qué meses fueron pagados por cada cliente":"Track which months each client has been paid - verify no months are missing"}
          </div>
        </div>
        <div style={{display:"flex",gap:".5rem",marginBottom:"1rem",flexWrap:"wrap",alignItems:"center"}}>
          <div style={{position:"relative",flex:1,minWidth:180}}>
            <Search size={12} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--c-text3,#45688a)",pointerEvents:"none"}}/>
            <input value={timelineSearch} onChange={e=>setTimelineSearch(e.target.value)}
              placeholder={lang==="es"?"Buscar cliente...":"Search client..."}
              className="dkt-search" style={{paddingLeft:30}}/>
          </div>
          <MultiSelectFilter options={allCarriersTimeline} selected={timelineCarrierSel} onChange={setTimelineCarrierSel} allLabel={lang==="es"?"Todas las Aseguradoras":"All Carriers"} icon={<FileText size={13} style={{color:"var(--c-text3,#45688a)"}}/>} lang={lang}/>
          {allStatesTimeline.length > 0 && <MultiSelectFilter options={allStatesTimeline} selected={timelineStateSel} onChange={setTimelineStateSel} allLabel={lang==="es"?"Todos los Estados":"All States"} icon={<span style={{fontSize:".8rem"}}>📍</span>} lang={lang} width={150}/>}
          <div style={{fontSize:".7rem",color:"var(--c-text3,#45688a)",fontFamily:"var(--font-mono)",whiteSpace:"nowrap"}}>{timelineRows.length} {lang==="es"?"clientes":"clients"} · {timelineMonths.length} {lang==="es"?"meses":"months"}</div>
          {/* Scroll the wide table left/right without hunting for the bottom scrollbar (Lissette #1). */}
          <div style={{display:"flex",gap:".25rem",marginLeft:"auto"}}>
            <button type="button" onClick={()=>{timelineScrollRef.current?.focus();scrollTimeline(-320);}} title={lang==="es"?"Mover a la izquierda (flecha ←)":"Scroll left (← key)"}
              style={{background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,color:"var(--c-white,#eef6ff)",cursor:"pointer",padding:".4rem .7rem",fontSize:".8rem",fontWeight:700}}>◀</button>
            <button type="button" onClick={()=>{timelineScrollRef.current?.focus();scrollTimeline(320);}} title={lang==="es"?"Mover a la derecha (flecha →)":"Scroll right (→ key)"}
              style={{background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:8,color:"var(--c-white,#eef6ff)",cursor:"pointer",padding:".4rem .7rem",fontSize:".8rem",fontWeight:700}}>▶</button>
          </div>
        </div>
        {timelineMonths.length === 0 ? (
          <div className="dkt-card" style={{textAlign:"center",padding:"3rem",color:"var(--c-text3,#45688a)"}}>
            <div style={{fontSize:"2rem",marginBottom:".5rem"}}>📭</div>
            <div style={{fontSize:".82rem"}}>{lang==="es"?"Sube archivos de comisiones para ver el timeline":"Upload commission files to see the timeline"}</div>
          </div>
        ) : (
          <>
          <div style={{fontSize:".66rem",color:"var(--c-text3,#45688a)",marginBottom:".4rem",display:"flex",alignItems:"center",gap:".4rem",flexWrap:"wrap"}}>
            <span>💡 {lang==="es"?"Haz clic en la tabla y usa las flechas ← → del teclado para moverte, o los botones ◀ ▶.":"Click the table and use the ← → arrow keys to move, or the ◀ ▶ buttons."}</span>
            <span style={{color:"#a78bfa"}}>· 😴 {lang==="es"?"Inactivo":"Inactive"}</span>
            <span style={{color:"var(--c-amber,#ffb700)"}}>· ⚠️ {lang==="es"?"No en CRM":"Not in CRM"}</span>
          </div>
          <div ref={timelineScrollRef} tabIndex={0}
            onKeyDown={e=>{ if(e.key==="ArrowRight"){e.preventDefault();scrollTimeline(160);} else if(e.key==="ArrowLeft"){e.preventDefault();scrollTimeline(-160);} else if(e.key==="Home"){e.preventDefault();timelineScrollRef.current?.scrollTo({left:0,behavior:"smooth"});} else if(e.key==="End"){e.preventDefault();timelineScrollRef.current?.scrollTo({left:timelineScrollRef.current.scrollWidth,behavior:"smooth"});} }}
            style={{overflow:"auto",maxHeight:"70vh",border:"1px solid var(--c-border,#1a3550)",borderRadius:12,outline:"none"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:".78rem"}}>
              <thead>
                <tr style={{background:"var(--c-surface,#0d1c2e)",borderBottom:"2px solid var(--c-border,#1a3550)"}}>
                  <th style={{textAlign:"left",padding:".5rem .6rem",fontWeight:700,color:"var(--c-white,#eef6ff)",fontFamily:"var(--font-display)",minWidth:128,position:"sticky",left:0,top:0,background:"var(--c-surface,#0d1c2e)",zIndex:3}}>{lang==="es"?"Cliente":"Client"}</th>
                  <th style={{textAlign:"left",padding:".5rem .4rem",fontWeight:600,color:"var(--c-text2,#7aa3c0)",minWidth:78,fontFamily:"var(--font-mono)",fontSize:".64rem",position:"sticky",top:0,background:"var(--c-surface,#0d1c2e)",zIndex:2}}>Carrier</th>
                  {timelineMonths.map(m=>(
                    <th key={m} style={{textAlign:"center",padding:".5rem .3rem",fontWeight:700,color:"var(--c-cyan,#00d4ff)",fontFamily:"var(--font-mono)",fontSize:".64rem",whiteSpace:"nowrap",minWidth:48,position:"sticky",top:0,background:"var(--c-surface,#0d1c2e)",zIndex:2}}>
                      {fmtMonth(m).replace(" 20"," '")}
                    </th>
                  ))}
                  <th style={{textAlign:"center",padding:".5rem .35rem",fontWeight:700,color:"var(--c-white,#eef6ff)",fontFamily:"var(--font-mono)",fontSize:".66rem",minWidth:56,position:"sticky",top:0,background:"var(--c-surface2,#112538)",zIndex:2}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {timelineRows.map((g,ri)=>{
                  const {client,carriers,states,rows,notInCrm,rawName}=g;
                  const rowTotal = rows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
                  const mainCarrier = carriers[0]||"--";
                  const inactive = client?.status==="inactive";
                  const key = client ? `c:${client.id}` : `n:${rawName}`;
                  return (
                    <tr key={key} style={{borderBottom:"1px solid var(--c-border,#1a3550)",background:ri%2===0?"transparent":"rgba(255,255,255,.015)",opacity:inactive?.75:1}}>
                      <td style={{padding:".45rem .6rem",fontWeight:600,fontSize:".76rem",color:"var(--c-white,#eef6ff)",position:"sticky",left:0,background:ri%2===0?"var(--c-bg2,#0b1622)":"var(--c-surface,#0d1c2e)",zIndex:1,whiteSpace:"nowrap"}}>
                        <div style={{display:"flex",alignItems:"center",gap:".4rem"}}>
                          {client ? <Avatar client={client} size={6}/> : <span style={{width:24,height:24,borderRadius:"50%",background:"var(--c-amber-dim,rgba(255,183,0,.12))",border:"1px solid rgba(255,183,0,.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:".7rem",color:"var(--c-amber,#ffb700)",flexShrink:0}}>?</span>}
                          <span>{client ? `${client.lastName}, ${client.firstName}` : rawName}</span>
                          {inactive && <span title={lang==="es"?"Inactivo":"Inactive"} style={{fontSize:".62rem",color:"#a78bfa",background:"rgba(167,139,250,.12)",border:"1px solid rgba(167,139,250,.25)",borderRadius:5,padding:"0 .35rem",fontWeight:700}}>😴 {lang==="es"?"Inactivo":"Inactive"}</span>}
                          {notInCrm && <span title={lang==="es"?"No está en el CRM":"Not in CRM"} style={{fontSize:".62rem",color:"var(--c-amber,#ffb700)",background:"var(--c-amber-dim,rgba(255,183,0,.1))",border:"1px solid rgba(255,183,0,.25)",borderRadius:5,padding:"0 .35rem",fontWeight:700}}>⚠️ {lang==="es"?"No en CRM":"Not in CRM"}</span>}
                        </div>
                      </td>
                      <td style={{padding:".45rem .45rem",color:"var(--c-cyan,#00d4ff)",fontSize:".66rem",fontFamily:"var(--font-mono)",whiteSpace:"nowrap"}}>
                        {mainCarrier}{carriers.length>1?` +${carriers.length-1}`:""}
                        {states.length>0 && <span style={{color:"var(--c-text3,#45688a)",marginLeft:".3rem"}}>· {states.join(", ")}</span>}
                      </td>
                      {timelineMonths.map(m=>{
                        const mrows = rows.filter(r=>r.month===m);
                        if (mrows.length) { const amt = mrows.reduce((s,r)=>s+parseFloat(r.amount||0),0); const neg = amt < 0; return (
                          <td key={m} style={{textAlign:"center",padding:".32rem .25rem",background:neg?"rgba(255,61,107,.12)":"rgba(0,255,136,.1)",color:neg?"var(--c-red,#ff3d6b)":"var(--c-green,#00ff88)",fontWeight:700,fontFamily:"var(--font-mono)",fontSize:".68rem",whiteSpace:"nowrap",border:`1px solid ${neg?"rgba(255,61,107,.18)":"rgba(0,255,136,.08)"}`}}>
                            {fmtMoney(amt)}
                          </td>
                        ); }
                        return (
                          <td key={m} style={{textAlign:"center",padding:".32rem .25rem",background:"rgba(255,61,107,.05)",color:"var(--c-text3,#45688a)",border:"1px solid rgba(255,61,107,.06)"}}>--</td>
                        );
                      })}
                      <td style={{textAlign:"center",padding:".45rem .35rem",fontWeight:700,color:Number(rowTotal)<0?"var(--c-red,#ff3d6b)":"var(--c-cyan,#00d4ff)",fontFamily:"var(--font-display)",fontSize:".8rem",whiteSpace:"nowrap",background:"var(--c-surface2,#112538)"}}>{fmtMoney(rowTotal)}</td>
                    </tr>
                  );
                })}
                {timelineRows.length===0&&(
                  <tr><td colSpan={timelineMonths.length+3} style={{textAlign:"center",padding:"2.5rem",color:"var(--c-text3,#45688a)",fontSize:".8rem"}}>{lang==="es"?"No hay clientes con comisiones para este filtro":"No clients with commission records"}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          </>
        )}
      </>}
      {tab==="notincrm" && <>
        <div style={{marginBottom:"1rem"}}>
          <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:"1rem",color:"var(--c-white,#eef6ff)",marginBottom:".25rem"}}>⚠️ {lang==="es"?"No Están en el CRM":"Not in CRM"}</div>
          <div style={{fontSize:".72rem",color:"var(--c-text3,#45688a)"}}>
            {lang==="es"?"Nombres del archivo de comisiones que no coincidieron con ningún cliente":"Names from commission files that didn't match any client in your CRM"}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:".5rem",marginBottom:"1rem"}}>
          <div style={{display:"flex",alignItems:"center",gap:".5rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:9,padding:".4rem .75rem"}}>
            <Calendar size={12} style={{color:"var(--c-text3,#45688a)"}}/>
            <select value={month} onChange={e=>setMonth(e.target.value)} style={{background:"transparent",border:"none",outline:"none",fontWeight:700,fontSize:".8rem",color:"var(--c-white,#eef6ff)",cursor:"pointer"}}>
              {months.map(m=><option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
          </div>
          <div style={{fontSize:".7rem",color:"var(--c-amber,#ffb700)",fontFamily:"var(--font-mono)",fontWeight:600}}>{notInCRM.length} records . {fmtMoney(notInCRMTotal)}</div>
        </div>
        <div className="dkt-card" style={{overflow:"hidden"}}>
          {notInCRM.length === 0 ? (
            <div style={{textAlign:"center",padding:"3rem",color:"var(--c-text3,#45688a)"}}>
              <div style={{fontSize:"2rem",marginBottom:".5rem"}}>✅</div>
              <div style={{fontSize:".82rem"}}>{lang==="es"?"¡Todo coincide! No hay registros sin cliente.":"All records matched! No unmatched commissions."}</div>
            </div>
          ) : (
            <>
              <div style={{padding:".5rem 1rem",background:"var(--c-amber-dim)",borderBottom:"1px solid rgba(255,183,0,.15)",display:"flex",alignItems:"center",gap:".5rem"}}>
                <AlertTriangle size={12} style={{color:"var(--c-amber,#ffb700)"}}/>
                <span style={{fontSize:".7rem",fontWeight:700,color:"var(--c-amber,#ffb700)",textTransform:"uppercase",letterSpacing:".08em",fontFamily:"var(--font-mono)"}}>NOT IN CRM - {notInCRM.length} records . {fmtMoney(notInCRMTotal)}</span>
              </div>
              {notInCRM.map((rec,i)=>(
                <div key={rec.id||i} style={{display:"flex",alignItems:"center",gap:".75rem",padding:".65rem 1rem",borderBottom:"1px solid var(--c-border,#1a3550)"}}>
                  <div style={{width:34,height:34,borderRadius:"50%",background:"var(--c-amber-dim)",border:"1px solid rgba(255,183,0,.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:".75rem",fontWeight:700,color:"var(--c-amber,#ffb700)",flexShrink:0,fontFamily:"var(--font-display)"}}>?</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:".84rem",color:"var(--c-white,#eef6ff)"}}>{rec.clientNameRaw||rec.name||"Unknown"}</div>
                    <div style={{display:"flex",gap:".5rem",marginTop:2}}>
                      <span style={{fontSize:".68rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",color:"var(--c-text2,#7aa3c0)",padding:".1rem .45rem",borderRadius:6,fontFamily:"var(--font-mono)"}}>{rec.carrier||"--"}</span>
                      {rec.memberIdRaw&&<span style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontFamily:"var(--font-mono)"}}>#{rec.memberIdRaw}</span>}
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:".9rem",color:"var(--c-amber,#ffb700)"}}>{fmtMoney(rec.amount)}</div>
                    <div style={{fontSize:".62rem",color:"var(--c-text3,#45688a)",fontFamily:"var(--font-mono)"}}>{fmtMonth(rec.month)}</div>
                  </div>
                  {canAddClientFromComm&&(
                    <button type="button" onClick={()=>addClientFromRec(rec)}
                      title={lang==="es"?"Crear este cliente en el CRM y enlazarlo":"Create this client in the CRM and link it"}
                      style={{fontSize:".68rem",fontWeight:700,background:"rgba(6,214,160,.12)",border:"1px solid rgba(6,214,160,.35)",color:"#06d6a0",borderRadius:7,padding:".32rem .55rem",cursor:"pointer",whiteSpace:"nowrap",fontFamily:"inherit",flexShrink:0}}>
                      ＋ {lang==="es"?"Cliente":"Add client"}
                    </button>
                  )}
                  {canEditComm&&(
                    <ClientPicker clients={clients} lang={lang} onPick={cid=>reassignCommission(rec, cid)}/>
                  )}
                  {canDeleteComm&&<button onClick={()=>deleteRecord(rec.id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",padding:4,borderRadius:6}} onMouseEnter={e=>e.currentTarget.style.color="var(--c-red,#ff3d6b)"} onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Trash2 size={13}/></button>}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="dkt-phase" style={{marginTop:".75rem"}}>
          💡 {lang==="es"?"Estos registros no se pudieron emparejar automáticamente. Puedes agregarlos manualmente al CRM y luego re-subir el archivo para que coincidan.":"These records couldn't be auto-matched. Add them to CRM first, then re-upload the file to match them."}
        </div>
      </>}
      {tab==="chargebacks" && (()=>{
        const allNeg = normalizedComms.filter(c => (yearFilter === "all" || c.month?.startsWith(yearFilter)) && c.amount < 0);
        const cbMonths = [...new Set(allNeg.map(r=>r.month).filter(Boolean))].sort();
        const cbMonth = month !== "all" && cbMonths.includes(month) ? month : "all";
        const cbCarriers = [...new Set(allNeg.filter(r=>cbMonth==="all"||r.month===cbMonth).map(r=>r.carrier).filter(Boolean))].sort();
        const cbStates = [...new Set(allNeg.filter(r=>cbMonth==="all"||r.month===cbMonth).map(r=>commState(r)).filter(Boolean))].sort();
        const filtered = allNeg.filter(r => {
          if (cbMonth !== "all" && r.month !== cbMonth) return false;
          if (!carrierMatch(r)) return false;
          if (!stateMatch(r)) return false;
          return true;
        }).sort((a,b)=>(a.clientNameRaw||a.name||"").localeCompare(b.clientNameRaw||b.name||"", undefined, { sensitivity:"base" }));
        const filteredTotal = filtered.reduce((s,r) => s + r.amount, 0);
        return <>
        <div style={{marginBottom:"1rem"}}>
          <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:"1rem",color:"var(--c-white,#eef6ff)",marginBottom:".25rem"}}>⛔ Chargebacks — {yearFilter}</div>
          <div style={{fontSize:".72rem",color:"var(--c-text3,#45688a)"}}>{lang==="es"?"Comisiones negativas / devoluciones de los carriers":"Negative commissions / carrier clawbacks"}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:".6rem",marginBottom:"1rem",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:".5rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:9,padding:".4rem .75rem"}}>
            <Calendar size={12} style={{color:"var(--c-text3,#45688a)"}}/>
            <select value={cbMonth} onChange={e=>setMonth(e.target.value)} style={{background:"transparent",border:"none",outline:"none",fontWeight:700,fontSize:".8rem",color:"var(--c-white,#eef6ff)",cursor:"pointer"}}>
              <option value="all">{lang==="es"?`Todo ${yearFilter}`:`All ${yearFilter}`}</option>
              {cbMonths.map(m=><option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
          </div>
          {cbCarriers.length > 0 && <MultiSelectFilter options={cbCarriers} selected={carrierSel} onChange={setCarrierSel} allLabel={lang==="es"?"Todas las Compañías":"All Carriers"} icon={<FileText size={12} style={{color:"var(--c-text3,#45688a)"}}/>} lang={lang}/>}
          {cbStates.length > 0 && <MultiSelectFilter options={cbStates} selected={stateSel} onChange={setStateSel} allLabel={lang==="es"?"Todos los Estados":"All States"} icon={<span style={{fontSize:".8rem"}}>📍</span>} lang={lang} width={150}/>}
          <div style={{background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.25)",borderRadius:10,padding:".5rem .85rem",display:"flex",alignItems:"center",gap:".5rem"}}>
            <span style={{fontFamily:"var(--font-display)",fontWeight:800,fontSize:"1rem",color:"#ef4444"}}>{fmtMoney(filteredTotal)}</span>
            <span style={{fontSize:".65rem",color:"rgba(239,68,68,.7)",fontFamily:"var(--font-mono)"}}>{filtered.length} {lang==="es"?"registros":"records"}</span>
          </div>
        </div>
        <div className="dkt-card" style={{overflow:"hidden"}}>
          {filtered.length===0?(
            <div style={{textAlign:"center",padding:"3rem",color:"var(--c-text3,#45688a)"}}>
              <div style={{fontSize:"2rem",marginBottom:".5rem"}}>✅</div>
              <div style={{fontSize:".82rem"}}>{lang==="es"?"No hay chargebacks":"No chargebacks found"}</div>
            </div>
          ):(
            <>
              <div style={{padding:".5rem 1rem",background:"rgba(239,68,68,.06)",borderBottom:"1px solid rgba(239,68,68,.12)",display:"grid",gridTemplateColumns:"1fr auto auto auto",gap:".5rem",fontSize:".65rem",fontWeight:700,color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".06em",fontFamily:"var(--font-mono)"}}>
                <span>{lang==="es"?"Cliente":"Client"}</span><span>Carrier</span><span>{lang==="es"?"Mes":"Month"}</span><span>{lang==="es"?"Monto":"Amount"}</span>
              </div>
              {filtered.map((rec,i)=>{
                // El nombre de un chargeback era texto muerto: para ver de quién
                // era había que ir a buscarlo a mano al módulo de Clientes
                // (Lissette 2026-08-17). Se resuelve con el mismo emparejador
                // que usa el resto de la pantalla; si no casa con nadie, se
                // queda como estaba en vez de fingir un enlace roto.
                const cbClient = clientById.get(resolvedClientId(rec));
                return (
                <div key={rec.id||i} style={{display:"grid",gridTemplateColumns:"1fr auto auto auto",gap:".5rem",padding:".6rem 1rem",borderBottom:"1px solid var(--c-border,#1a3550)",alignItems:"center"}}>
                  <div style={{minWidth:0}}>
                    {cbClient
                      ? <ClientName client={cbClient} color="var(--c-white,#eef6ff)" carrier={rec.carrier}/>
                      : <div style={{fontWeight:700,fontSize:".8rem",color:"var(--c-white,#eef6ff)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rec.clientNameRaw||"Unknown"}</div>}
                  </div>
                  <span style={{fontSize:".68rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",color:"var(--c-text2,#7aa3c0)",padding:".1rem .45rem",borderRadius:6,fontFamily:"var(--font-mono)"}}>{rec.carrier||"--"}</span>
                  <span style={{fontSize:".65rem",color:"var(--c-text3,#45688a)",fontFamily:"var(--font-mono)"}}>{fmtMonth(rec.month)}</span>
                  <div style={{display:"flex",alignItems:"center",gap:".4rem"}}>
                    <span style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:".85rem",color:"#ef4444"}}>{fmtMoney(rec.amount)}</span>
                    {canDeleteComm&&<button onClick={()=>deleteRecord(rec.id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-text3,#45688a)",padding:2,borderRadius:4}} onMouseEnter={e=>e.currentTarget.style.color="#ef4444"} onMouseLeave={e=>e.currentTarget.style.color="var(--c-text3,#45688a)"}><Trash2 size={12}/></button>}
                  </div>
                </div>
                );
              })}
            </>
          )}
        </div>
      </>;
      })()}
      {tab==="history" && <>
        <div style={{marginBottom:"1rem"}}>
          <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:"1rem",color:"var(--c-white,#eef6ff)",marginBottom:".25rem"}}>🗂️ {lang==="es"?"Historial de Subidas":"Upload History"}</div>
          <div style={{fontSize:".72rem",color:"var(--c-text3,#45688a)"}}>{lang==="es"?"Archivos importados — puedes eliminar subidas por error":"Imported files — you can delete uploads made by mistake"}</div>
        </div>
        {uploadBatches.length===0 ? (
          <div className="dkt-card" style={{textAlign:"center",padding:"3rem",color:"var(--c-text3,#45688a)"}}>
            <div style={{fontSize:"2rem",marginBottom:".5rem"}}>📭</div>
            <div style={{fontSize:".82rem"}}>{lang==="es"?"No hay subidas registradas":"No uploads recorded"}</div>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:".5rem"}}>
            {uploadBatches.map(b=>{
              const fn = b.fileName;
              const displayFiles = fn ? [fn] : (b.fileList.length ? b.fileList : [b.carriers.join(", ") || "Upload"]);
              const mainName = fn || (b.fileList.length > 0 ? `${b.fileList.length} ${lang==="es"?"archivos":"files"}` : b.carriers.join(", ") || "Upload");
              const ext = (fn||displayFiles[0]||"").split(".").pop().toLowerCase();
              const icon = ext==="pdf"?"📄":(ext==="xlsx"||ext==="xls")?"📊":"📋";
              const timeStr = b.ts ? new Date(b.ts).toLocaleString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"}) : b.date;
              return (
              <div key={b.key} className="dkt-card" style={{padding:".85rem 1rem",display:"flex",alignItems:"center",gap:"1rem"}}>
                <div style={{width:42,height:42,borderRadius:12,background:"var(--c-cyan-dim,rgba(0,212,255,.12))",border:"1px solid rgba(0,212,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.1rem",flexShrink:0}}>{icon}</div>
                <div style={{flex:1,minWidth:0}}>
                  {fn ? (
                    <div style={{fontWeight:700,fontSize:".8rem",color:"var(--c-white,#eef6ff)",wordBreak:"break-all"}}>{fn}</div>
                  ) : (
                    <div>
                      <div style={{fontWeight:700,fontSize:".8rem",color:"var(--c-white,#eef6ff)",marginBottom:3}}>{mainName}</div>
                      {b.fileList.length > 0 && <div style={{display:"flex",flexDirection:"column",gap:2}}>
                        {b.fileList.map((f,i)=><div key={i} style={{fontSize:".62rem",color:"var(--c-text2,#7aa3c0)",fontFamily:"var(--font-mono)"}}>{f.endsWith(".pdf")||f.endsWith(".PDF")?"📄":"📊"} {f}</div>)}
                      </div>}
                    </div>
                  )}
                  <div style={{display:"flex",gap:".4rem",flexWrap:"wrap",marginTop:4}}>
                    <span style={{fontSize:".64rem",background:"var(--c-green-dim,rgba(0,255,136,.1))",border:"1px solid rgba(0,255,136,.2)",color:"var(--c-green,#00ff88)",padding:".1rem .45rem",borderRadius:6,fontFamily:"var(--font-mono)",fontWeight:700}}>{b.count} {lang==="es"?"registros":"records"}</span>
                    {b.carriers.map(c=><span key={c} style={{fontSize:".64rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",color:"var(--c-text2,#7aa3c0)",padding:".1rem .45rem",borderRadius:6,fontFamily:"var(--font-mono)"}}>{c}</span>)}
                  </div>
                  <div style={{fontSize:".62rem",color:"var(--c-text3,#45688a)",fontFamily:"var(--font-mono)",marginTop:3}}>{b.months.map(m=>fmtMonth(m)).join(", ")}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:".9rem",color:"var(--c-green,#00ff88)"}}>{fmtMoney(b.total)}</div>
                  <div style={{fontSize:".6rem",color:"var(--c-text3,#45688a)",fontFamily:"var(--font-mono)"}}>{timeStr}</div>
                </div>
                {canDeleteComm&&<button onClick={()=>setDeleteConfirm({key:b.key,label:mainName,count:b.count})} style={{background:"none",border:"1px solid rgba(239,68,68,.2)",borderRadius:8,cursor:"pointer",color:"var(--c-text3,#45688a)",padding:".4rem",transition:"all .2s"}} onMouseEnter={e=>{e.currentTarget.style.color="#ef4444";e.currentTarget.style.borderColor="rgba(239,68,68,.5)";e.currentTarget.style.background="rgba(239,68,68,.08)";}} onMouseLeave={e=>{e.currentTarget.style.color="var(--c-text3,#45688a)";e.currentTarget.style.borderColor="rgba(239,68,68,.2)";e.currentTarget.style.background="none";}}><Trash2 size={14}/></button>}
              </div>);
            })}
          </div>
        )}
        {uploadBatches.length > 0 && <div style={{marginTop:".75rem",padding:".75rem 1rem",background:"var(--c-surface2,#112538)",border:"1px solid var(--c-border,#1a3550)",borderRadius:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:".75rem",fontWeight:700,color:"var(--c-text2,#7aa3c0)"}}>{uploadBatches.length} {lang==="es"?"archivos":"files"} · {cuadre.deSubidas.filas} {lang==="es"?"registros de subidas":"records from uploads"}</span>
          <span style={{fontFamily:"var(--font-display)",fontWeight:800,fontSize:"1rem",color:"var(--c-green,#00ff88)"}}>{fmtMoney(cuadre.deSubidas.total)}</span>
        </div>}
        {/* LO QUE NO VIENE DE NINGUNA SUBIDA. Se dice con su numero y su monto
            —no se ofrece borrar en lote— para que el historial explique el 100%
            del dinero: es lo unico que permite cuadrar contra el estado de
            cuenta de la aseguradora. Callarlo es lo que hacia que borrarlo todo
            dejara el historial vacio y el total sin cuadrar. */}
        {cuadre.fuera.filas > 0 && <div style={{marginTop:".5rem",padding:".8rem 1rem",background:"rgba(245,158,11,.08)",border:"1px solid rgba(245,158,11,.3)",borderRadius:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:"1rem",flexWrap:"wrap"}}>
            <span style={{fontSize:".75rem",fontWeight:700,color:"var(--c-amber,#f59e0b)"}}>
              {lang==="es"
                ?`${cuadre.fuera.filas} comisión(es) no vienen de ninguna subida`
                :`${cuadre.fuera.filas} commission(s) don't come from any upload`}
            </span>
            <span style={{fontFamily:"var(--font-display)",fontWeight:800,fontSize:".95rem",color:"var(--c-amber,#f59e0b)"}}>{fmtMoney(cuadre.fuera.total)}</span>
          </div>
          <div style={{fontSize:".68rem",color:"var(--c-text3,#45688a)",marginTop:".35rem",lineHeight:1.5}}>
            {lang==="es"
              ?"Son las apuntadas a mano y las que llegaron sin quedar atadas a un archivo. Suman en el total, así que si el monto no cuadra, están aquí."
              :"The ones entered by hand plus any that arrived without being tied to a file. They do count in the total, so if the amount doesn't tie out, they are here."}
          </div>
          {/* VERLAS Y PODER BORRARLAS. Enseñar el descuadre sin dejar tocarlo
              era medio arreglo: se veia el dinero de sobra y no habia forma de
              quitarlo desde aqui (Lissette, 2026-09-11). Se ven una por una
              ANTES de borrar, porque entre ellas hay apuntadas a mano. */}
          <div style={{display:"flex",gap:".5rem",marginTop:".6rem",flexWrap:"wrap"}}>
            <button type="button" onClick={()=>setVerFuera(v=>!v)}
              style={{padding:".35rem .7rem",borderRadius:8,border:"1px solid rgba(245,158,11,.4)",background:"transparent",color:"var(--c-amber,#f59e0b)",fontSize:".7rem",fontWeight:700,cursor:"pointer"}}>
              {verFuera?(lang==="es"?"Ocultar":"Hide"):(lang==="es"?`Ver las ${cuadre.fuera.filas}`:`Show the ${cuadre.fuera.filas}`)}
            </button>
            {canDeleteComm&&<button type="button" onClick={()=>setConfirmFuera(true)}
              style={{padding:".35rem .7rem",borderRadius:8,border:"1px solid rgba(239,68,68,.4)",background:"rgba(239,68,68,.08)",color:"#ef4444",fontSize:".7rem",fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:5}}>
              <Trash2 size={12}/>{lang==="es"?`Borrar las ${cuadre.fuera.filas}`:`Delete the ${cuadre.fuera.filas}`}
            </button>}
          </div>
          {verFuera && <div style={{marginTop:".6rem",maxHeight:320,overflowY:"auto",display:"flex",flexDirection:"column",gap:".3rem"}}>
            {fueraDeSubidas.map(c=>(
              <div key={c.id} style={{display:"flex",alignItems:"center",gap:".6rem",padding:".45rem .6rem",borderRadius:7,background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border,#1a3550)"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:".74rem",color:"var(--c-white,#eef6ff)",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.clientNameRaw||(lang==="es"?"(sin nombre)":"(no name)")}</div>
                  <div style={{fontSize:".64rem",color:"var(--c-text3,#45688a)",fontFamily:"var(--font-mono)"}}>{c.carrier||"—"} · {c.month?fmtMonth(c.month):"—"}</div>
                </div>
                <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:".8rem",color:"var(--c-green,#00ff88)",flexShrink:0}}>{fmtMoney(c.amount)}</div>
                {canDeleteComm&&<button type="button" title={lang==="es"?"Eliminar esta comisión":"Delete this commission"} onClick={()=>borrarUnaDeFuera(c.id)}
                  style={{background:"none",border:"1px solid rgba(239,68,68,.2)",borderRadius:7,cursor:"pointer",color:"var(--c-text3,#45688a)",padding:".3rem",flexShrink:0}}>
                  <Trash2 size={12}/>
                </button>}
              </div>
            ))}
          </div>}
          <div style={{marginTop:".5rem",paddingTop:".5rem",borderTop:"1px solid rgba(245,158,11,.2)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:".72rem",fontWeight:800,color:"var(--c-text2,#7aa3c0)"}}>{lang==="es"?"Total de todas las comisiones":"All commissions total"}</span>
            <span style={{fontFamily:"var(--font-display)",fontWeight:800,fontSize:"1rem",color:"var(--c-white,#eef6ff)"}}>{fmtMoney(cuadre.total)}</span>
          </div>
        </div>}
        {deleteConfirm && <div style={{position:"fixed",inset:0,zIndex:100,background:"rgba(0,0,0,.6)",backdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setDeleteConfirm(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--c-surface,#0d1c2e)",border:"1px solid var(--c-border2,#1f3f60)",borderRadius:16,padding:"1.5rem",maxWidth:420,width:"90%",boxShadow:"0 20px 60px rgba(0,0,0,.5)"}}>
            <div style={{fontSize:"2rem",textAlign:"center",marginBottom:".75rem"}}>⚠️</div>
            <div style={{fontWeight:800,fontSize:".95rem",color:"var(--c-white,#eef6ff)",textAlign:"center",marginBottom:".5rem"}}>{lang==="es"?"¿Estás seguro?":"Are you sure?"}</div>
            <div style={{fontSize:".78rem",color:"var(--c-text2,#7aa3c0)",textAlign:"center",marginBottom:"1.25rem"}}>
              {lang==="es"
                ?`Esto eliminará ${deleteConfirm.count} registros de "${deleteConfirm.label}". Esta acción no se puede deshacer.`
                :`This will delete ${deleteConfirm.count} records from "${deleteConfirm.label}". This action cannot be undone.`}
            </div>
            <div style={{display:"flex",gap:".5rem"}}>
              <button onClick={()=>setDeleteConfirm(null)} style={{flex:1,padding:".6rem",borderRadius:10,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontWeight:700,fontSize:".82rem",cursor:"pointer"}}>{lang==="es"?"Cancelar":"Cancel"}</button>
              <button disabled={borrandoSubida} onClick={()=>deleteBatch(deleteConfirm.key)} style={{flex:1,padding:".6rem",borderRadius:10,border:"none",background:"#dc2626",color:"#fff",fontWeight:700,fontSize:".82rem",cursor:borrandoSubida?"wait":"pointer",opacity:borrandoSubida?.6:1}}>{borrandoSubida?(lang==="es"?"Borrando…":"Deleting…"):(lang==="es"?"Sí, Eliminar":"Yes, Delete")}</button>
            </div>
          </div>
        </div>}
      </>}
      {/* BORRAR LO QUE NO VIENE DE NINGUNA SUBIDA. Con los dos numeros delante
          y diciendo que ahi dentro puede haber apuntadas a mano: es un borrado
          que no se deshace, asi que la decision tiene que ser informada. */}
      {confirmFuera && <Modal title={lang==="es"?"⚠️ Borrar lo que no viene de ninguna subida":"⚠️ Delete what doesn't come from any upload"} onClose={()=>!borrandoFuera&&setConfirmFuera(false)}>
        <div style={{fontSize:".85rem",color:"var(--c-white,#eef6ff)",fontWeight:700,marginBottom:".5rem"}}>
          {lang==="es"
            ?`Se eliminarán ${cuadre.fuera.filas} comisión(es) por ${fmtMoney(cuadre.fuera.total)}.`
            :`This deletes ${cuadre.fuera.filas} commission(s) totalling ${fmtMoney(cuadre.fuera.total)}.`}
        </div>
        <div style={{fontSize:".78rem",color:"var(--c-text2,#7aa3c0)",lineHeight:1.6,marginBottom:"1rem"}}>
          {lang==="es"
            ?"Dejan de contar en el total inmediatamente. Entre ellas pueden estar las que alguien apuntó a mano — conviene mirarlas antes con el botón «Ver». Esto no se puede deshacer."
            :"They stop counting in the total right away. Some of them may have been entered by hand — worth listing them first with the Show button. This cannot be undone."}
        </div>
        <div style={{display:"flex",gap:".5rem"}}>
          <button type="button" disabled={borrandoFuera} onClick={()=>setConfirmFuera(false)}
            style={{flex:1,padding:".6rem",borderRadius:10,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontWeight:700,fontSize:".82rem",cursor:"pointer"}}>
            {lang==="es"?"Cancelar":"Cancel"}
          </button>
          <button type="button" disabled={borrandoFuera} onClick={borrarLoDeFuera}
            style={{flex:1,padding:".6rem",borderRadius:10,border:"none",background:"#dc2626",color:"#fff",fontWeight:700,fontSize:".82rem",cursor:borrandoFuera?"wait":"pointer",opacity:borrandoFuera?.6:1}}>
            {borrandoFuera?(lang==="es"?"Borrando…":"Deleting…"):(lang==="es"?`Sí, borrar ${cuadre.fuera.filas}`:`Yes, delete ${cuadre.fuera.filas}`)}
          </button>
        </div>
      </Modal>}
      {/* ESTE ARCHIVO YA SE SUBIO. Pregunta, no error: la aseguradora puede
          mandar el mismo nombre cada mes, asi que rechazarlo de plano impediria
          subir el mes siguiente. Se enseñan los numeros de la subida anterior
          —cuando, cuantas filas, cuanto— porque son con los que una persona lo
          decide en un segundo. */}
      {dupAviso && <Modal title={lang==="es"?"⚠️ Este archivo ya se subió":"⚠️ This file was already uploaded"} onClose={()=>!dupAviso.subiendo&&setDupAviso(null)}>
        <div style={{fontSize:".8rem",color:"var(--c-text2,#7aa3c0)",lineHeight:1.6,marginBottom:".9rem"}}>
          {lang==="es"
            ?"Si lo subes otra vez, esas comisiones se guardan DE NUEVO y el monto del mes se duplica."
            :"If you upload it again, those commissions are saved AGAIN and the month's total doubles."}
        </div>
        {(dupAviso.duplicados||[]).map((d,i)=>(
          <div key={i} style={{padding:".7rem .8rem",borderRadius:10,marginBottom:".5rem",background:"var(--c-surface2,#112538)",border:`1px solid ${d.identica?"rgba(239,68,68,.4)":"var(--c-border,#1a3550)"}`}}>
            <div style={{fontWeight:700,fontSize:".78rem",color:"var(--c-white,#eef6ff)",wordBreak:"break-all"}}>{d.nombre}</div>
            <div style={{fontSize:".7rem",color:"var(--c-text2,#7aa3c0)",fontFamily:"var(--font-mono)",marginTop:3}}>
              {lang==="es"?"Subido el":"Uploaded on"} {d.fecha||"—"} · {d.filas} {lang==="es"?"registros":"records"} · {fmtMoney(d.total)}
            </div>
            {d.identica && <div style={{fontSize:".68rem",color:"#ef4444",fontWeight:700,marginTop:4}}>
              {lang==="es"
                ?"Mismo número de registros y mismo monto: es el mismo archivo."
                :"Same record count and same amount: it is the same file."}
            </div>}
          </div>
        ))}
        <div style={{display:"flex",gap:".5rem",marginTop:"1rem"}}>
          <button type="button" disabled={dupAviso.subiendo} onClick={()=>setDupAviso(null)}
            style={{flex:1,padding:".6rem",borderRadius:10,border:"1px solid var(--c-border,#1a3550)",background:"var(--c-surface2,#112538)",color:"var(--c-text2,#7aa3c0)",fontWeight:700,fontSize:".82rem",cursor:"pointer"}}>
            {lang==="es"?"No subirlo":"Don't upload"}
          </button>
          <button type="button" disabled={dupAviso.subiendo}
            onClick={async()=>{ setDupAviso(a=>({...a,subiendo:true})); await confirmImport(true); }}
            style={{flex:1,padding:".6rem",borderRadius:10,border:"none",background:"#d97706",color:"#fff",fontWeight:700,fontSize:".82rem",cursor:dupAviso.subiendo?"wait":"pointer",opacity:dupAviso.subiendo?.6:1}}>
            {dupAviso.subiendo?(lang==="es"?"Subiendo…":"Uploading…"):(lang==="es"?"Subirlo igual":"Upload anyway")}
          </button>
        </div>
      </Modal>}
      {uploadModal && <Modal title={`📂 ${lang==="es"?"Verificación de Comisiones":"Commission Verification"}${/^\d{4}-\d{2}$/.test(month)?` - ${fmtMonth(month)}`:""}`} onClose={()=>{setUploadModal(false);setMatchResults(null);setCommFiles([]);setParseError("");setDupAviso(null);}} wide>
        <div className="space-y-4">
          {!matchResults ? <>
            <div>
              <div style={{fontSize:".65rem",fontWeight:800,color:"var(--c-text3,#45688a)",textTransform:"uppercase",letterSpacing:".08em",marginBottom:".5rem",fontFamily:"var(--font-mono)"}}>
                {lang==="es"?"Tipo de póliza de este archivo":"Policy type for this file"}
              </div>
              <div style={{fontSize:".68rem",color:"var(--c-text3,#45688a)",marginBottom:".5rem"}}>
                {lang==="es"
                  ? "Si el archivo trae una columna de producto (p. ej. Florida Blue con \"prod\" = health/dental), cada fila se clasifica sola por esa columna. Este tipo se usa solo para las filas que no la traigan."
                  : "If the file has a product column (e.g. Florida Blue \"prod\" = health/dental), each row is auto-classified by it. This type is only used for rows without one."}
              </div>
              <div style={{display:"flex",gap:".35rem",flexWrap:"wrap"}}>
                {COMM_POLICY_TYPES.filter(pt=>pt.id!=="all").map(pt=>(
                  <button key={pt.id} type="button"
                    onClick={()=>setUploadPolicyType(pt.id)}
                    style={{
                      padding:".38rem .75rem",borderRadius:8,cursor:"pointer",
                      background:uploadPolicyType===pt.id?pt.color+"22":"var(--c-surface2,#112538)",
                      border:`2px solid ${uploadPolicyType===pt.id?pt.color:"var(--c-border,#1a3550)"}`,
                      color:uploadPolicyType===pt.id?pt.color:"var(--c-text2,#7aa3c0)",
                      fontWeight:uploadPolicyType===pt.id?800:600,fontSize:".72rem",
                      transition:"all .18s",display:"flex",alignItems:"center",gap:".35rem",
                    }}>
                    {pt.icon} {lang==="es"?pt.label:pt.labelEn}
                  </button>
                ))}
              </div>
            </div>
            <Input label={lang==="es"?"Aseguradora (opcional)":"Carrier name (optional)"} value={carrierName} onChange={e=>setCarrierName(e.target.value)} placeholder="Aetna, Humana, CareFirst... (el sistema lo detecta del archivo)"/>
            <div
              onDragOver={e=>{e.preventDefault();}}
              onDrop={handleFileDrop}
              className="border-2 border-dashed border-blue-200 rounded-2xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all bg-gray-50"
              onClick={()=>document.getElementById("comm-file-multi").click()}>
              <input id="comm-file-multi" type="file" accept=".csv,.xlsx,.xls,.pdf" multiple className="hidden" onChange={handleFileDrop}/>
              <div className="text-4xl mb-2">📂</div>
              <p className="text-sm font-bold text-gray-700">{lang==="es"?"Arrastra tus archivos aquí":"Drag your files here"}</p>
              <p className="text-xs text-gray-400 mt-1">Excel (.xlsx) . CSV . PDF - puedes seleccionar varios archivos a la vez</p>
              <div className="mt-3 inline-flex px-4 py-1.5 bg-blue-600 text-white rounded-xl text-xs font-semibold">📁 {lang==="es"?"Seleccionar Archivos":"Select Files"}</div>
            </div>
            {parseError && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">{parseError}</div>}
            {commFiles.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1.5">
                <p className="text-xs font-bold text-emerald-700">✅ {commFiles.length} archivo{commFiles.length>1?"s":""} cargados - detección automática lista:</p>
                {commFiles.map((f,i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5">
                      <span>{f.ext==="xlsx"||f.ext==="xls"?"📊":f.ext==="pdf"?"📄":"📋"}</span>
                      <span className="font-semibold text-gray-800">{f.name}</span>
                    </span>
                    <span className="text-emerald-600 font-bold">{f.records.length} registros detectados</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-xs mt-1 pt-1 border-t border-emerald-200">
                  <span className="font-bold text-emerald-800">📊 Total</span>
                  <span className="text-emerald-700 font-bold">{commFiles.reduce((s,f)=>s+f.records.length,0)} registros</span>
                </div>
                <p className="text-[10px] text-emerald-500 mt-1">✨ Columnas detectadas automáticamente - no necesitas configurar nada</p>
              </div>
            )}
            {allCommRecords.length > 0 && (
              <button onClick={runFuzzyMatch} disabled={isRunning}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-3 rounded-xl text-sm shadow-md disabled:opacity-50">
                {isRunning ? `⏳ ${lang==="es"?"Procesando...":"Processing..."}` : `🚀 ${lang==="es"?`Verificar ${allCommRecords.length} Registros - Buscar Clientes`:`Verify ${allCommRecords.length} Records - Match Clients`}`}
              </button>
            )}
          </> : <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1">
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-emerald-500"/>
                <span className="text-sm text-emerald-700 font-semibold">
                  {matchResults.filter(r=>r.matched).length} de {matchResults.length} {lang==="es"?"identificados":"matched"} ({matchResults.filter(r=>r.matched&&!r.isInactive).length} {lang==="es"?"activos":"active"}, {matchResults.filter(r=>r.isInactive).length} {lang==="es"?"inactivos":"inactive"})
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {matchResults.filter(r=>!r.confirmed).length} {lang==="es"?"se guardarán como 'Not in CRM' (no marcados — nada se descarta)":"will be saved as 'Not in CRM' (unchecked — nothing is dropped)"}
              </div>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {(() => {
                // Sorted A→Z by file name. Keep the original index so the
                // checkbox/assign mutations still hit the right entry.
                const view = matchResults
                  .map((r, origIdx) => ({ r, origIdx }))
                  .sort((a, b) => (a.r.rawName || "").localeCompare(b.r.rawName || "", undefined, { sensitivity: "base" }));
                return view.map(({ r, origIdx }) => (
                <div key={origIdx} className={`flex items-center gap-3 p-3 rounded-xl border ${r.confirmed?"border-emerald-200 bg-emerald-50/50":r.isInactive?"border-purple-200 bg-purple-50/50":"border-amber-200 bg-amber-50/50"}`}>
                  <input type="checkbox" checked={r.confirmed} onChange={()=>setMatchResults(prev=>prev.map((x,j)=>j===origIdx?{...x,confirmed:!x.confirmed}:x))}
                    className="w-4 h-4 rounded accent-blue-600"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500">{lang==="es"?"Archivo:":"File:"} <span className="font-mono text-gray-700">{r.rawName}</span></div>
                    <div className="text-sm font-semibold text-gray-900">
                      {r.matched ? <>
                        {r.isInactive ? "😴 " : "✅ "}{r.matched.firstName} {r.matched.lastName}
                        {r.remembered
                          ? <span className="text-xs font-semibold text-indigo-500 ml-1">🔗 {lang==="es"?"Asignado (recordado)":"Assigned (remembered)"}</span>
                          : <span className="text-xs font-normal text-blue-500 ml-1">{r.matchScore}%</span>}
                        {r.isInactive && <span className="text-xs text-purple-500 ml-1">(Inactive)</span>}
                      </> : <span className="text-amber-600">⚠️ {lang==="es"?"No identificado":"Not matched"}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-emerald-600">{fmtMoney(r.amount)}</div>
                    <div className="text-[10px] text-gray-400">{r.carrier}</div>
                  </div>
                  {r.matched ? (
                    // Fix a wrong assignment without starting over (Lissette #3):
                    // "Cambiar" clears just this row back to the picker.
                    <button type="button"
                      onClick={()=>setMatchResults(prev=>prev.map((x,j)=>j===origIdx?{...x,matched:null,confirmed:false,remembered:false,matchScore:0}:x))}
                      title={lang==="es"?"Cambiar / corregir el cliente asignado":"Change / fix the assigned client"}
                      style={{fontSize:".7rem",fontWeight:700,background:"#fff",border:"1px solid #cbd5e1",color:"#334155",borderRadius:7,padding:".32rem .6rem",cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                      ✏️ {lang==="es"?"Cambiar":"Change"}
                    </button>
                  ) : (
                    <ClientPicker clients={clients} lang={lang} onPick={cid=>{ const c=clients.find(x=>x.id===cid); setMatchResults(prev=>prev.map((x,j)=>j===origIdx?{...x,matched:c,confirmed:!!c,matchScore:100}:x)); }}/>
                  )}
                </div>
                ));
              })()}
            </div>
            <div className="flex gap-2">
              <button onClick={()=>setMatchResults(null)} className="flex-1 bg-gray-100 text-gray-700 font-semibold py-2.5 rounded-xl text-sm">{lang==="es"?"← Volver":"← Back"}</button>
              <button onClick={()=>confirmImport(false)} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-2.5 rounded-xl text-sm">
                ✅ {lang==="es"?"Confirmar":"Confirm"} {matchResults.filter(r=>r.confirmed).length} + {matchResults.filter(r=>!r.confirmed).length} {lang==="es"?"sin CRM":"unmatched"}
              </button>
            </div>
          </>}
        </div>
      </Modal>}
      {manualModal && <Modal title={`✏️ ${lang==="es"?"Agregar Comisión Manual":"Add Manual Commission"}`} onClose={()=>setManualModal(false)}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cliente</label>
            <ClientSearchSelect clients={clients} value={manualForm.clientId} onChange={e=>setManualForm(f=>({...f,clientId:e.target.value}))}
              lang={lang} noneLabel="-- Seleccionar cliente --"
              optionLabel={(c)=>`${c.firstName} ${c.lastName} (${c.carrier||"--"})`}
              selectClassName="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-blue-400" />
          </div>
          <Input label="Carrier / Aseguradora" value={manualForm.carrier} onChange={e=>setManualForm(f=>({...f,carrier:e.target.value}))} placeholder=""/>
          <Input label="Monto ($)" type="number" step="0.01" min="0" value={manualForm.amount} onChange={e=>setManualForm(f=>({...f,amount:e.target.value}))} placeholder="0.00"/>
          <Input label="Member ID (opcional)" value={manualForm.memberId} onChange={e=>setManualForm(f=>({...f,memberId:e.target.value}))} placeholder=""/>
          <button onClick={addManual} disabled={!manualForm.clientId||!manualForm.amount}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2">
            <Save size={16}/> Guardar
          </button>
        </div>
      </Modal>}
      {addClientForm && <Modal title={`➕ ${lang==="es"?"Nuevo Cliente":"New Client"}`} onClose={()=>{ if(!savingClient) setAddClientForm(null); }}>
        <div className="space-y-4">
          <p style={{fontSize:".78rem",color:"var(--c-text2,#7aa3c0)",margin:0}}>
            {lang==="es"
              ? "Se crea aquí mismo y se enlazará solo a esta comisión. Puedes completar el resto del perfil después en Clientes."
              : "Created right here and auto-linked to this commission. You can fill the rest of the profile later in Clients."}
          </p>
          <Input label={lang==="es"?"Nombre":"First name"} value={addClientForm.firstName} onChange={e=>setAddClientForm(f=>({...f,firstName:e.target.value}))} placeholder=""/>
          <Input label={lang==="es"?"Apellido":"Last name"} value={addClientForm.lastName} onChange={e=>setAddClientForm(f=>({...f,lastName:e.target.value}))} placeholder=""/>
          <Input label={lang==="es"?"Teléfono (opcional)":"Phone (optional)"} value={addClientForm.phone} onChange={e=>setAddClientForm(f=>({...f,phone:e.target.value}))} placeholder=""/>
          <Input label={lang==="es"?"Email (opcional)":"Email (optional)"} type="email" value={addClientForm.email} onChange={e=>setAddClientForm(f=>({...f,email:e.target.value}))} placeholder=""/>
          {/* ¿Va al módulo Prospecto y Referido? (Lissette 2026-07-11) */}
          <div style={{border:"1px solid var(--c-border,#1a3550)",borderRadius:10,padding:".6rem .7rem",background:"var(--c-surface2,#112538)"}}>
            <div style={{fontSize:".76rem",fontWeight:700,color:"var(--c-text,#c8dff0)",marginBottom:".45rem"}}>
              {lang==="es"?"¿Enviar al módulo de Prospecto y Referido?":"Send to the Prospects & Referrals module?"}
            </div>
            <div style={{display:"flex",gap:".5rem"}}>
              {[[true,lang==="es"?"Sí, como prospecto":"Yes, as prospect"],[false,lang==="es"?"No, solo la ficha":"No, just the profile"]].map(([val,lbl])=>{
                const on = !!addClientForm.toProspect === val;
                return (
                  <button key={String(val)} type="button" onClick={()=>setAddClientForm(f=>({...f,toProspect:val}))}
                    style={{flex:1,padding:".4rem .5rem",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontSize:".74rem",fontWeight:700,
                      border:`1px solid ${on?"rgba(0,212,255,.5)":"var(--c-border,#1a3550)"}`,
                      background:on?"var(--c-cyan-dim,rgba(0,212,255,.12))":"transparent",
                      color:on?"var(--c-cyan,#00d4ff)":"var(--c-text3,#45688a)"}}>
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={saveNewClient} disabled={savingClient || !((addClientForm.firstName||"").trim() || (addClientForm.lastName||"").trim())}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2">
            <Save size={16}/> {savingClient ? (lang==="es"?"Guardando…":"Saving…") : (lang==="es"?"Guardar y enlazar":"Save & link")}
          </button>
        </div>
      </Modal>}
    </div>
  );
}
export function ReportsView({ policies, clients, commissions, onCommissionsChange, onClientsChange, onPoliciesChange, metricsPerms, onMetricsPermsChange, tasks, docs, signatures, users, currentUser, t, lang, policyTypes, userPrefs = {}, onOpenClient }) {
  const tr=t.reports;
  const [tab,setTab]=useState("commissions");
  const policyMix=policyTypes.map(pt=>({ type:pt, count:policies.filter(p=>p.type===pt.id).length })).filter(x=>x.count>0);
  // Reclamos needs "which clients already received a commission this year".
  // It lives in CommissionTracker's scope, which is a SIBLING render — not a
  // parent — so referencing it here threw a ReferenceError and crashed the tab
  // the moment it was opened. Compute it from the same props instead.
  const commissionClientIds = useMemo(
    () => computeCommissionClientIds(commissions, clients),
    [commissions, clients],
  );
  return <div>
    <div style={{display:"flex",gap:".5rem",marginBottom:"1.25rem",flexWrap:"wrap",alignItems:"center"}}>
      {[["commissions","💰 "+(lang==="es"?"Comisiones":"Commissions")],["renewals",tr.tabRenewals],["mix",tr.tabPolicies],["claims","📋 "+(lang==="es"?"Reclamos":"Claims")]].map(([id,label])=>(
        <button key={id} onClick={()=>setTab(id)} className={`dkt-tab${tab===id?" on":""}`}>{label}</button>
      ))}
    </div>
    {tab==="commissions" && <CommissionTracker clients={clients} policies={policies} commissions={commissions} onCommissionsChange={onCommissionsChange} onClientsChange={onClientsChange} users={users} currentUser={currentUser} t={t} lang={lang} policyTypes={policyTypes} onClientClick={onOpenClient} />}
    {tab==="renewals"&&(()=>{
      // Este módulo enseñaba SIEMPRE 30 y 60 días, dijeras lo que dijeras en
      // Preferencias. Si pones que las de salud te avisen con 4 meses, este es
      // el sitio donde vas a trabajar esas renovaciones — y te seguía enseñando
      // 30 días (Lissette 2026-08-17: "que también se vayan al módulo de
      // renovación"). Ahora cada póliza usa el plazo de SU tipo.
      const porVencer = policies
        .map(pl=>{ const d=daysUntil(pl.renewalDate); return { pl, d, lead: renewalLeadDaysForType(userPrefs, pl.type) }; })
        .filter(x=>x.d!==null&&x.d>=0&&x.d<=x.lead)
        .sort((a,b)=>a.d-b.d);
      const vencidas = policies.filter(pl=>{ const d=daysUntil(pl.renewalDate); return d!==null&&d<0; });
      const activas = policies.filter(pl=>pl.status==="active");
      const clientById2 = new Map((clients||[]).map(c=>[c.id,c]));
      const leadTxt = (n)=> n%30===0 && n>=30
        ? `${n/30} ${lang==="es"?(n===30?"mes":"meses"):(n===30?"month":"months")}`
        : `${n} ${lang==="es"?"días":"days"}`;
      return <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Calendar} label={lang==="es"?"Por vencer (según tus avisos)":"Coming up (per your alerts)"} value={porVencer.length} accent="bg-amber-500"/>
        <StatCard icon={Calendar} label={lang==="es"?"Vencidas":"Overdue"} value={vencidas.length} accent="bg-red-500"/>
        <StatCard icon={Calendar} label={lang==="es"?"Activas":"Active"} value={activas.length} accent="bg-emerald-500"/>
      </div>
      <div style={{fontSize:".72rem",color:"var(--c-text3,#45688a)",marginBottom:".75rem",lineHeight:1.45}}>
        {lang==="es"
          ? "El plazo de cada póliza sale de Perfil → Preferencias → Avisos, y puede ser distinto por tipo (salud, auto, vida…)."
          : "Each policy's window comes from Profile → Preferences → Alerts, and can differ per type (health, auto, life…)."}
      </div>
      {porVencer.length===0 ? (
        <div className="dkt-card" style={{textAlign:"center",padding:"2.5rem",color:"var(--c-text3,#45688a)"}}>
          <div style={{fontSize:"2rem",marginBottom:".5rem"}}>✅</div>
          <div style={{fontSize:".82rem"}}>{lang==="es"?"Ninguna póliza entra en el plazo que configuraste.":"No policy falls inside the window you configured."}</div>
        </div>
      ) : (
        <div className="dkt-card" style={{overflow:"hidden"}}>
          {porVencer.map(({pl,d,lead})=>{
            const c=clientById2.get(pl.clientId);
            const pt=(policyTypes||[]).find(x=>x.id===pl.type);
            return (
              <div key={pl.id} style={{display:"flex",alignItems:"center",gap:".75rem",padding:".6rem 1rem",borderBottom:"1px solid var(--c-border,#1a3550)"}}>
                <span style={{fontSize:"1.1rem"}}>{pt?.icon||"📄"}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:".82rem",fontWeight:600,color:"var(--c-white,#eef6ff)"}}>
                    {c && onOpenClient
                      ? <button type="button" onClick={()=>onOpenClient(c)} style={{background:"none",border:"none",padding:0,cursor:"pointer",color:"var(--c-cyan,#00d4ff)",fontWeight:600,fontSize:".82rem",fontFamily:"inherit"}}>{c.firstName} {c.lastName}</button>
                      : (c?`${c.firstName} ${c.lastName}`:"--")}
                  </div>
                  <div style={{fontSize:".66rem",color:"var(--c-text3,#45688a)"}}>
                    {(pt&&(lang==="es"?pt.labelEs:pt.label))||pl.type} · {pl.carrier||"--"} · {fmt(pl.renewalDate)}
                    <span style={{marginLeft:6,opacity:.8}}>({lang==="es"?"aviso":"alert"}: {leadTxt(lead)})</span>
                  </div>
                </div>
                <span style={{fontSize:".72rem",fontWeight:700,fontFamily:"var(--font-mono)",color:d<=30?"var(--c-red,#ff3d6b)":"var(--c-amber,#ffb700)",whiteSpace:"nowrap"}}>
                  {d===0?(lang==="es"?"HOY":"TODAY"):`${d} ${lang==="es"?"días":"days"}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>;
    })()}
    {tab==="mix"&&<div>
      <div className="grid md:grid-cols-2 gap-4">
        {policyMix.map(({type:pt,count})=><div key={pt.id} className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{backgroundColor:pt.color+"20"}}>{pt.icon}</div>
          <div className="flex-1"><div className="font-semibold text-gray-900">{lang==="es"?pt.labelEs:pt.label}</div><div className="text-xs text-gray-400">{count} {count===1?"policy":"policies"}</div></div>
          <div className="text-2xl font-bold text-gray-900 font-mono">{count}</div>
        </div>)}
      </div>
    </div>}
    {tab==="claims" && <ClaimsReportView policies={policies} clients={clients} users={users} lang={lang} onOpenClient={onOpenClient} onPoliciesChange={onPoliciesChange} commissionClientIds={commissionClientIds}/>}
  </div>;
}
